import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { createPortal } from 'react-dom';
import {
  Paper,
  Typography,
  Box,
  Button,
  IconButton,
  Divider,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import NotesIcon from '@mui/icons-material/Notes';
import PrintIcon from '@mui/icons-material/Print';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RouteIcon from '@mui/icons-material/Route';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import html2canvas from 'html2canvas';
import FlightTile from './FlightTile';
import DroppableColumn from './DroppableColumn';
import PrintDispatchButton from './PrintDispatchButton';
import RouteMapModal from './RouteMapModal';
import ReviewPopup from './ReviewPopup';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import { useNavigate } from 'react-router-dom';
import RateLimitDialog from '../RateLimitDialog';
import SolverLoadingModal from './SolverLoadingModal';
import { getConnectionCategory, isOvernightFlight, calculateConnectionTime, physicalStopCount } from '../../utils/connectionUtils';
import {
  computeUnassignedTiles,
  isHotOrPriority,
  getFwdAftBags,
  shouldSplitFlight,
  shouldCreateSingleCompartmentFlight,
  shouldAutoSplitMixed,
  isFreightOnlyFlight,
  createMixedSplitFlights,
  createSplitFlights,
  createSingleCompartmentFlight,
} from '../../utils/boardTileProcessor';

const cloneRuns = (runs) => JSON.parse(JSON.stringify(runs));

// Paste shortcut label — ⌘V on macOS, Ctrl+V everywhere else.
const PASTE_SHORTCUT =
  typeof navigator !== 'undefined' && /Mac/i.test(navigator.userAgent)
    ? '⌘V'
    : 'Ctrl+V';

// Helper function to get gate range for flights
const getGateRange = (gateNum) => {
    if (isNaN(gateNum)) return null;
    if (gateNum >= 20 && gateNum <= 29) return '20s';
    if (gateNum >= 30 && gateNum <= 39) return '30s';
    if (gateNum >= 40 && gateNum <= 49) return '40s';
    if (gateNum >= 50 && gateNum <= 59) return '50s';
    if (gateNum >= 60 && gateNum <= 69) return '60s';
    return null;
};

// Helper functions for FWD/AFT splitting now live in utils/boardTileProcessor
// and are imported above. Single source of truth shared with the dev bench.

// Helper constants for auto-assignment logic
const sectors = {
  sector1: Array.from({ length: 17 }, (_, i) => i + 23),
  sector2: Array.from({ length: 16 }, (_, i) => i + 40),
  sector3: Array.from({ length: 12 }, (_, i) => i + 56)
};
const quadrants = {
  nw: [23, 25, 27, 29, 31, 33, 35, 37, 39, 41, 43],
  sw: [24, 26, 28, 30, 32, 34, 36, 38, 40, 42, 44, 46],
  ne: [45, 47, 49, 51, 53, 55, 57, 59, 61, 63, 65, 67],
  se: [48, 50, 52, 54, 56, 58, 60, 62, 64, 66]
};

function DispatchBoard({ tdrData, brockData, freightManifestData, params, inboundETA, inboundGate, flightNote = '', setFlightNote, tdrMissingFlights = [] }) {
  // Ghost tiles: Brock-scanned flights the TDR has no row for. Confirm and
  // dismiss decisions live OUTSIDE runs so they survive tile re-derivation
  // (split-mode toggles etc. rebuild the unassigned column from scratch).
  // Like everything else on the board they are session state: there is no
  // undo while you work, and a reload starts the decisions fresh.
  const [confirmedGhostIds, setConfirmedGhostIds] = useState(() => new Set());
  const [dismissedGhostIds, setDismissedGhostIds] = useState(() => new Set());
  const [runs, setRuns] = useState({
    unassigned: [],
    drivers: []
  });

  // State for drag overlay
  const [activeFlight, setActiveFlight] = useState(null);
  
  // State for split mode
  const [splitMode, setSplitMode] = useState(false);
  
  // State for add mode
  const [addMode, setAddMode] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  
  // State for editing drivers
  const [editingDriverId, setEditingDriverId] = useState(null);
  const [editingDriverNotes, setEditingDriverNotes] = useState(null);
  const [editingDriverName, setEditingDriverName] = useState('');
  const [editingNotes, setEditingNotes] = useState('');
  
  // State for hiding cold gates
  const [hideColdGates, setHideColdGates] = useState(true);
  const initialRunsRef = useRef(null);
  const lastFlightNumberRef = useRef(null); // Track flight to reset initialRunsRef on new flight

  // State for route map modal
  const [routeModalDriver, setRouteModalDriver] = useState(null);

  const navigate = useNavigate();

  // State for flight note modal (note for all drivers)
  const [flightNoteModalOpen, setFlightNoteModalOpen] = useState(false);
  const [editingFlightNote, setEditingFlightNote] = useState('');

  // State for magic button loading modal
  const [magicLoading, setMagicLoading] = useState(false);

  // Share-to-Teams button state + capture targets
  const [shareStatus, setShareStatus] = useState('idle'); // 'idle' | 'busy' | 'copied' | 'downloaded' | 'failed'
  const headerCaptureRef = useRef(null);
  const boardGridRef = useRef(null);

  // Rate-limit dialog state. Triggered by the 429 catch on Magic Button.
  const [rateLimitOpen, setRateLimitOpen] = useState(false);
  const [rateLimitSeconds, setRateLimitSeconds] = useState(0);

  // Items the solver flagged for the dispatcher to confirm or fix after a
  // Magic Button run (cancelled flights, unparseable gates).
  const [reviewItems, setReviewItems] = useState([]);
  const [reviewOpen, setReviewOpen] = useState(false);
  // Outbound gate: PRINT RUNS and SHARE TO TEAMS pause when undecided
  // ghost tiles remain — the plan is leaving the desk with an open call.
  // { action: 'print' | 'share' } while the dialog is up, else null.
  const [ghostGate, setGhostGate] = useState(null);

  // State for custom flight modal
  const [customFlightModalOpen, setCustomFlightModalOpen] = useState(false);
  const [customFlightData, setCustomFlightData] = useState({
    flightNumber: '',
    destination: '',
    etd: '',
    gate: '',
    bags: 0,
    bagBins: { A: '', B: '', C: '', D: '', E: '', F: '' }, // Bag bin distribution
    connectionCategory: 'normal',
    notes: '',
    freight: [] // Array of freight items
  });

  // State for adding freight items
  const [freightInput, setFreightInput] = useState({
    type: '',
    pieces: '',
    weight: '',
    bins: { A: '', B: '', C: '', D: '', E: '', F: '' }
  });

  useEffect(() => {
    if (!tdrData || !brockData) {
      const emptyState = { unassigned: [], drivers: [] };
      setRuns(emptyState);
      initialRunsRef.current = cloneRuns(emptyState);
      lastFlightNumberRef.current = null;
      return;
    }

    // Check if this is a new flight - if so, reset the initial runs ref
    const currentFlightNumber = tdrData?.inbound?.flightNumber || '';
    if (lastFlightNumberRef.current !== currentFlightNumber) {
      initialRunsRef.current = null; // Reset so it gets set fresh below
      lastFlightNumberRef.current = currentFlightNumber;
    }

    // Tile derivation lives in utils/boardTileProcessor.computeUnassignedTiles.
    // The local helper below remains only to support the legacy local-freight
    // flight-number lookup further down. Everything else is delegated.
    const normalizeFlightNumber = (flightNum) => {
      if (!flightNum) return '';
      const wnMatch = flightNum.match(/WN\s*(\d+)/i);
      if (wnMatch && wnMatch[1]) return parseInt(wnMatch[1], 10).toString();
      const fiMatch = flightNum.match(/FI\s*0*(\d+)/i);
      if (fiMatch && fiMatch[1]) return `FI ${parseInt(fiMatch[1], 10)}`;
      const tkMatch = flightNum.match(/TK\s*0*(\d+)/i);
      if (tkMatch && tkMatch[1]) return `TK ${parseInt(tkMatch[1], 10)}`;
      return flightNum;
    };

    setRuns(prevRuns => {
      // Get all flight IDs that are currently assigned to drivers (including their split versions)
      const assignedFlightIds = new Set();
      prevRuns.drivers.forEach(driver => {
        driver.flights.forEach(flight => {
          // Add the flight's unique ID
          assignedFlightIds.add(flight.uniqueId);
          // Only reserve the original ID for unsplit flights
          if (!flight.isSplit && !flight.isColdMixedSplit && flight.originalId) {
            assignedFlightIds.add(flight.originalId);
          }
          // For non-split flights, also add any potential split versions
          if (!flight.isSplit) {
            assignedFlightIds.add(`${flight.uniqueId}-FWD`);
            assignedFlightIds.add(`${flight.uniqueId}-AFT`);
          }
        });
      });

      // Delegate the full pipeline (TDR+Brock merge → category → split →
      // sort → same-destination detection) to the shared boardTileProcessor
      // so the dev bench tests the EXACT same code path.
      const sortedFlights = computeUnassignedTiles({
        tdrData,
        brockData,
        params,
        inboundETA,
        inboundGate,
        splitMode,
        assignedFlightIds,
      });

      // Ghost tiles: flights Brock scanned that the TDR has no row for.
      // Real bags, unconfirmed plan — they join the bottom of Unassigned
      // as dashed provisional tiles built from the scan record, and stay
      // out of Autoroute until a human confirms them.
      const ghostFlights = (tdrMissingFlights || [])
        .filter((g) => !dismissedGhostIds.has(`ghost-${g.flightNumber}`))
        .filter((g) => !assignedFlightIds.has(`ghost-${g.flightNumber}`))
        .map((g) => {
          const etd = (g.est || g.sch || '').replace(/L$/, '');
          const connectionTime = etd && inboundETA
            ? calculateConnectionTime(etd, inboundETA, false)
            : null;
          const confirmed = confirmedGhostIds.has(`ghost-${g.flightNumber}`);
          return {
            uniqueId: `ghost-${g.flightNumber}`,
            flightNumber: g.flightNumber,
            originalFlightNumber: g.flightNumber,
            destination: g.destination || '',
            // Downline-tagged scans arrive as extra destinations under the
            // same number; render them as arrow rows like any other tile.
            destinations: (g.destinations && g.destinations.length
              ? g.destinations.map((d) => ({ city: d.destination || '', bags: d.bags || 0, freight: [] }))
              : [{ city: g.destination || '', bags: g.bags || 0, freight: [] }]),
            isMultiDestination: Boolean(g.destinations && g.destinations.length > 1),
            bags: g.bags || 0,
            binDistribution: g.binDistribution || {},
            gate: g.gate || '',
            etd,
            status: '',
            isOvernight: false,
            freight: [],
            allFreight: [],
            // Seeded as a REAL note: on the tile, editable, prints verbatim.
            // It stays through Confirm — the driver still needs the
            // heads-up even after the dispatcher accepts the tile.
            notes: 'NOT ON TDR - verify at the aircraft',
            connectionTime,
            connectionCategory: connectionTime != null
              ? getConnectionCategory(connectionTime, params, false)
              : 'normal',
            // The scan record IS the primary; no red reconciler pulse on
            // top of the ghost treatment.
            primaryConfirmed: true,
            isGhost: true,
            ghostConfirmed: confirmed,
          };
        });
      // Ghosts lead the Unassigned column — an open call belongs where
      // the dispatcher is already looking, not buried under the sort.
      const tilesWithGhosts = [...ghostFlights, ...sortedFlights];

      // Calculate local/terminating bags from Brock data
      let totalLocalBags = 0;
      const localBagsByBin = {};
      
      if (brockData && brockData.bins) {
        Object.entries(brockData.bins).forEach(([binLetter, binData]) => {
          if (binData.terminating && binData.terminating.totalBags) {
            const bagCount = binData.terminating.totalBags;
            totalLocalBags += bagCount;
            localBagsByBin[binLetter] = bagCount;
          }
        });
      }
      
      // Process local freight from freight manifest
      const localFreightFlights = [];
      if (freightManifestData && freightManifestData.terminating && freightManifestData.terminating.length > 0) {
        const flightInfo = freightManifestData.flight_info || {};
        const inboundFlightNumber = flightInfo.flight_number || '';
        const brockBins = brockData.bins || {};
        
        freightManifestData.terminating.forEach((item, index) => {
          // Determine freight type from description
          const description = (item.description || '').toUpperCase();
          let freightType = 'NFG'; // default
          if (description.includes('HUMAN')) freightType = 'HUM';
          else if (item.service_level === 'FF') freightType = 'FF_';
          else if (item.service_level === 'S') freightType = 'S__';
          
          // Try to find bin location from Brock data
          const freightByBin = {};
          let foundInBins = false;
          
          Object.entries(brockBins).forEach(([binName, binData]) => {
            const flights = binData.flights || binData;
            if (Array.isArray(flights)) {
              flights.forEach(flight => {
                const normalizedFlightNum = normalizeFlightNumber(flight.flightNumber);
                if (normalizedFlightNum === inboundFlightNumber && flight.freight > 0) {
                  if (!freightByBin[binName]) {
                    freightByBin[binName] = 0;
                  }
                  freightByBin[binName] += 1;
                  foundInBins = true;
                }
              });
            }
          });
          
          // If not found in bins, make a reasonable guess based on weight
          if (!foundInBins) {
            if (item.weight > 300) {
              freightByBin['E'] = item.pieces; // Heavy goes AFT
            } else if (item.weight < 100) {
              freightByBin['B'] = item.pieces; // Light goes FWD
            } else {
              freightByBin['C'] = Math.ceil(item.pieces / 2);
              freightByBin['D'] = Math.floor(item.pieces / 2);
            }
          }
          
          localFreightFlights.push({
            uniqueId: `local-freight-${index}`,
            flightNumber: 'LOCAL FREIGHT',
            originalFlightNumber: 'LOCAL FREIGHT',
            destination: '',  // Empty destination for local freight
            bags: 0,
            gate: '',  // No gate for local freight
            etd: '',  // No ETD for local freight
            connectionCategory: 'freight-only',  // This makes it orange!
            isLocal: false,  // Set to false so it doesn't use gray local styling
            isFreightOnly: true,
            isLocalFreight: true,  // Special flag for local freight
            freight: [{
              type: '',  // No type for local freight
              pieces: item.pieces,
              count: item.pieces,
              weight: item.weight
            }],
            allFreight: [{
              type: '',  // No type for local freight
              pieces: item.pieces,
              count: item.pieces,
              weight: item.weight
            }],
            freightByBin: freightByBin,
            binDistribution: {},
            notes: ''
          });
        });
      }
      
      // Filter out any existing local drivers from prevRuns.drivers
      const nonLocalDrivers = prevRuns.drivers.filter(d => !d.isLocalDriver);
      const localDrivers = [];
      
      if (totalLocalBags > 0 || localFreightFlights.length > 0) {
        // Create local bag "flights" (pseudo-flights for display)
        const createLocalFlight = (bags, binDistribution) => ({
          uniqueId: `local-${Date.now()}`,
          flightNumber: 'LOCAL',
          destination: '', // Empty destination for LOCAL flights
          bags: bags,
          binDistribution: binDistribution,
          gate: inboundGate || 'Terminal', // Use arrival gate
          etd: inboundETA || '', // Use arrival time instead of ETD
          connectionCategory: 'local',
          isLocal: true,
          notes: '',
          isThruBag: false, // Local drivers don't handle thru bags
          isSameGate: false, // Not applicable for local drivers
          needsSplit: totalLocalBags > 110 // Flag for red badge
        });
        
        // Always create single local driver - no auto-split
        const localId = Math.max(...prevRuns.drivers.map(d => typeof d.id === 'number' ? d.id : 0), 0) + 1000;
        
        // Combine local bag flight with local freight flights
        const localFlights = [];
        if (totalLocalBags > 0) {
          localFlights.push(createLocalFlight(totalLocalBags, localBagsByBin));
        }
        localFlights.push(...localFreightFlights);
        
        localDrivers.push({
          id: localId,
          name: 'Local Driver',
          notes: flightNote || '',
          isLocalDriver: true,
          flights: localFlights
        });
      }

      const nextRuns = {
        unassigned: tilesWithGhosts,
        drivers: [...nonLocalDrivers, ...localDrivers] // Preserve non-local drivers and add local ones
      };

      // Only set initial runs ref once on first load, not on subsequent re-renders
      if (!splitMode && !initialRunsRef.current) {
        initialRunsRef.current = cloneRuns(nextRuns);
      }

      return nextRuns;
    });
  }, [tdrData, brockData, params, inboundETA, inboundGate, splitMode, flightNote, tdrMissingFlights, confirmedGhostIds, dismissedGhostIds]);

  // Handle split mode toggle separately to avoid recalculating everything
  useEffect(() => {
    setRuns(prevRuns => {
      // Get all flight IDs that are currently assigned to drivers
      const assignedFlightIds = new Set();
      prevRuns.drivers.forEach(driver => {
        driver.flights.forEach(flight => {
          assignedFlightIds.add(flight.uniqueId);
          if (!flight.isSplit && !flight.isColdMixedSplit && flight.originalId) {
            assignedFlightIds.add(flight.originalId);
          }
          if (!flight.isSplit) {
            assignedFlightIds.add(`${flight.uniqueId}-FWD`);
            assignedFlightIds.add(`${flight.uniqueId}-AFT`);
          }
        });
      });

      // Process only unassigned flights for splitting
      const finalFlights = [];
      prevRuns.unassigned.forEach(flight => {
        // Skip if this flight is somehow assigned
        if (assignedFlightIds.has(flight.uniqueId)) return;

        // Handle already auto-split tiles (isColdMixedSplit) first, before trying to split them again
        if (flight.isColdMixedSplit && splitMode) {
          // This is already a bag-only or freight-only tile from auto-split
          // Just assign splitType based on bin distribution, don't re-split
          let processedFlight = { ...flight };

          if (flight.isBagsOnly && flight.binDistribution) {
            const fwdBags = (flight.binDistribution.A || 0) + (flight.binDistribution.B || 0) + (flight.binDistribution.C || 0);
            const aftBags = (flight.binDistribution.D || 0) + (flight.binDistribution.E || 0) + (flight.binDistribution.F || 0);

            if (fwdBags > 0 && aftBags === 0) {
              processedFlight.splitType = 'FWD';
            } else if (aftBags > 0 && fwdBags === 0) {
              processedFlight.splitType = 'AFT';
            }
          } else if (flight.isFreightOnly && flight.freightByBin) {
            const fwdFreight = (flight.freightByBin.A || 0) + (flight.freightByBin.B || 0) + (flight.freightByBin.C || 0);
            const aftFreight = (flight.freightByBin.D || 0) + (flight.freightByBin.E || 0) + (flight.freightByBin.F || 0);

            if (fwdFreight > 0 && aftFreight === 0) {
              processedFlight.splitType = 'FWD';
            } else if (aftFreight > 0) {
              processedFlight.splitType = 'AFT';
            }
          }

          finalFlights.push(processedFlight);
        } else if (shouldAutoSplitMixed(flight)) {
          // Auto-split mixed flights into freight and bag tiles
          const [freightFlight, bagFlight] = createMixedSplitFlights(flight);
          if (!assignedFlightIds.has(freightFlight.uniqueId)) {
            finalFlights.push(freightFlight);
          }
          if (!assignedFlightIds.has(bagFlight.uniqueId)) {
            finalFlights.push(bagFlight);
          }
        } else if (shouldSplitFlight(flight, splitMode)) {
          const [fwdFlight, aftFlight] = createSplitFlights(flight);
          if (!assignedFlightIds.has(fwdFlight.uniqueId)) {
            finalFlights.push(fwdFlight);
          }
          if (!assignedFlightIds.has(aftFlight.uniqueId)) {
            finalFlights.push(aftFlight);
          }
        } else if (shouldCreateSingleCompartmentFlight(flight, splitMode)) {
          // Create single compartment flight for flights with bags in only one compartment
          const singleCompartmentFlight = createSingleCompartmentFlight(flight);
          finalFlights.push(singleCompartmentFlight);
        } else {
          // Check if this is a freight-only flight and mark it for orange styling
          let processedFlight = isFreightOnlyFlight(flight) ?
            { ...flight, connectionCategory: 'freight-only' } :
            flight;

          // If in split mode, assign splitType based on bin distribution
          if (splitMode) {
            // Handle freight-only tiles
            if (isFreightOnlyFlight(flight) && flight.freightByBin) {
              const fwdFreight = (flight.freightByBin.A || 0) + (flight.freightByBin.B || 0) + (flight.freightByBin.C || 0);
              const aftFreight = (flight.freightByBin.D || 0) + (flight.freightByBin.E || 0) + (flight.freightByBin.F || 0);

              if (fwdFreight > 0 && aftFreight === 0) {
                processedFlight = { ...processedFlight, splitType: 'FWD', isSplit: true };
              } else if (aftFreight > 0) {
                processedFlight = { ...processedFlight, splitType: 'AFT', isSplit: true };
              }
            }
            // Handle bag-only tiles from auto-split (isColdMixedSplit means it's already been split from mixed flight)
            else if (flight.isColdMixedSplit && flight.isBagsOnly && flight.binDistribution) {
              const fwdBags = (flight.binDistribution.A || 0) + (flight.binDistribution.B || 0) + (flight.binDistribution.C || 0);
              const aftBags = (flight.binDistribution.D || 0) + (flight.binDistribution.E || 0) + (flight.binDistribution.F || 0);

              if (fwdBags > 0 && aftBags === 0) {
                processedFlight = { ...processedFlight, splitType: 'FWD', isSplit: true };
              } else if (aftBags > 0 && fwdBags === 0) {
                processedFlight = { ...processedFlight, splitType: 'AFT', isSplit: true };
              } else if (fwdBags > 0 && aftBags > 0) {
                // Has bags in both compartments - need to split into two tiles
                // This shouldn't happen often but handle it just in case
                processedFlight = { ...processedFlight, splitType: 'FWD', isSplit: true };
              }
            }
          }

          finalFlights.push(processedFlight);
        }
      });

      const sortedFlights = finalFlights.sort((a, b) => {
        const aIsHot = isHotOrPriority(a);
        const bIsHot = isHotOrPriority(b);
        if (aIsHot && !bIsHot) return -1;
        if (!aIsHot && bIsHot) return 1;

        // Overnight flights should always sort last (regardless of ETD)
        const aIsOvernight = a.isOvernight || false;
        const bIsOvernight = b.isOvernight || false;
        if (aIsOvernight && !bIsOvernight) return 1;  // a goes after b
        if (!aIsOvernight && bIsOvernight) return -1; // a goes before b

        // For ETD comparison, treat early morning times (00:00-04:59) as late night
        const parseETDForSort = (etd) => {
          if (!etd) return 0;
          const [hours, minutes] = etd.replace(/[LZ]/gi, '').split(':').map(n => Number(n) || 0);
          // If it's early morning (midnight to 5am), add 24 hours for sorting
          if (hours < 5) {
            return (hours + 24) * 60 + minutes;
          }
          return hours * 60 + minutes;
        };

        const aMinutes = parseETDForSort(a.etd);
        const bMinutes = parseETDForSort(b.etd);
        return aMinutes - bMinutes;
      });

      // Detect same-destination flights for consolidation opportunities
      const destinationFlightMap = new Map();
      sortedFlights.forEach(flight => {
        // Skip if this is a split of the same flight (bags vs freight)
        const baseFlightNumber = flight.flightNumber?.replace(/-FWD|-AFT|-BAGS|-FREIGHT/g, '');

        // Only count flights with destinations
        if (flight.destination) {
          const destination = flight.destination.toUpperCase();
          if (!destinationFlightMap.has(destination)) {
            destinationFlightMap.set(destination, []);
          }

          // Check if we already have this flight number (to avoid counting split tiles)
          const existingFlight = destinationFlightMap.get(destination).find(
            f => f.flightNumber?.replace(/-FWD|-AFT|-BAGS|-FREIGHT/g, '') === baseFlightNumber
          );

          if (!existingFlight) {
            destinationFlightMap.get(destination).push(flight);
          }
        }
      });

      // Mark flights that have same-destination opportunities
      sortedFlights.forEach(flight => {
        if (flight.destination) {
          const destination = flight.destination.toUpperCase();
          const sameDestFlights = destinationFlightMap.get(destination) || [];

          // Filter out the current flight and its splits
          const baseFlightNumber = flight.flightNumber?.replace(/-FWD|-AFT|-BAGS|-FREIGHT/g, '');
          const otherFlights = sameDestFlights.filter(
            f => f.flightNumber?.replace(/-FWD|-AFT|-BAGS|-FREIGHT/g, '') !== baseFlightNumber
          );

          if (otherFlights.length > 0) {
            flight.hasMultipleToSameDestination = true;
            flight.otherSameDestinationFlights = otherFlights.map(f => ({
              flightNumber: f.flightNumber,
              etd: f.etd
            }));
          }
        }
      });

      return {
        ...prevRuns,
        unassigned: sortedFlights
      };
    });
  }, [splitMode]);

  // *** THIS FUNCTION IS UPDATED ***
  const handleAddDriver = () => {
    setRuns(prevRuns => {
      // Get unique ID (still needs to be unique across all drivers)
      const newDriverId = (prevRuns.drivers.length > 0) ? Math.max(...prevRuns.drivers.map(d => d.id)) + 1 : 1;
      
      // Count only non-local drivers for naming
      const nonLocalDrivers = prevRuns.drivers.filter(d => !d.isLocalDriver);
      const driverNumber = nonLocalDrivers.length + 1;
      
      const newDriver = { id: newDriverId, name: `Driver ${driverNumber}`, notes: flightNote || '', flights: [] };

      // Prepend the new driver to the start of the array instead of appending it to the end.
      return { 
        ...prevRuns, 
        drivers: [newDriver, ...prevRuns.drivers] 
      };
    });
  };

  const handleRemoveDriver = (driverIdToRemove) => {
    setRuns(prevRuns => {
      const driverToRemove = prevRuns.drivers.find(d => d.id === driverIdToRemove);
      
      // Special handling for FWD/AFT local drivers
      if (driverToRemove && driverToRemove.isLocalDriver && 
          (driverToRemove.name.includes('FWD') || driverToRemove.name.includes('AFT'))) {
        
        // Find the complementary local driver
        const isFwd = driverToRemove.name.includes('FWD');
        const otherLocalDriver = prevRuns.drivers.find(d => 
          d.isLocalDriver && d.id !== driverIdToRemove &&
          (isFwd ? d.name.includes('AFT') : d.name.includes('FWD'))
        );
        
        if (otherLocalDriver) {
          // Combine the bags back together
          const removedFlight = driverToRemove.flights[0];
          const otherFlight = otherLocalDriver.flights[0];
          
          const combinedBags = (removedFlight?.bags || 0) + (otherFlight?.bags || 0);
          const combinedDistribution = {
            ...(removedFlight?.binDistribution || {}),
            ...(otherFlight?.binDistribution || {})
          };
          
          // Update the other driver to be a regular local driver
          const updatedDrivers = prevRuns.drivers
            .filter(d => d.id !== driverIdToRemove)
            .map(d => {
              if (d.id === otherLocalDriver.id) {
                return {
                  ...d,
                  name: 'Local Driver',
                  flights: [{
                    ...otherFlight,
                    uniqueId: `local-combined-${Date.now()}`,
                    bags: combinedBags,
                    binDistribution: combinedDistribution
                  }]
                };
              }
              return d;
            });
          
          return {
            ...prevRuns,
            drivers: updatedDrivers
          };
        }
      }
      
      // Don't allow removing regular local drivers
      if (driverToRemove && driverToRemove.isLocalDriver) {
        return prevRuns;
      }
      
      const flightsToReassign = driverToRemove ? driverToRemove.flights : [];
      
      // Filter out local flights - they shouldn't go to unassigned
      const reassignableFlights = flightsToReassign.filter(f => !f.isLocal);
      
      const remainingDrivers = prevRuns.drivers.filter(d => d.id !== driverIdToRemove);
      const newUnassigned = [...prevRuns.unassigned, ...reassignableFlights].sort((a, b) => {
        const aIsHot = isHotOrPriority(a);
        const bIsHot = isHotOrPriority(b);
        if (aIsHot && !bIsHot) return -1;
        if (!aIsHot && bIsHot) return 1;
        
        // Overnight flights should always sort last
        const aIsOvernight = a.isOvernight || false;
        const bIsOvernight = b.isOvernight || false;
        if (aIsOvernight && !bIsOvernight) return 1;
        if (!aIsOvernight && bIsOvernight) return -1;
        
        return (a.etd || '').localeCompare(b.etd || '');
      });
      return {
        unassigned: newUnassigned,
        drivers: remainingDrivers,
      };
    });
  };

  const toggleAddMode = () => {
    setAddMode(!addMode);
    if (!addMode) {
      setSelectedDriverId(null);
    }
  };

  const handleDriverSelect = (driverId) => {
    if (addMode) {
      setSelectedDriverId(selectedDriverId === driverId ? null : driverId);
    }
  };

  const handleEditDriverName = (driverId, currentName) => {
    setEditingDriverId(driverId);
    setEditingDriverName(currentName);
  };

  const handleEditDriverNotes = (driverId, currentNotes) => {
    setEditingDriverNotes(driverId);
    setEditingNotes(currentNotes || '');
  };

  const handleSaveDriverName = () => {
    setRuns(prev => ({
      ...prev,
      drivers: prev.drivers.map(driver =>
        driver.id === editingDriverId
          ? { ...driver, name: editingDriverName }
          : driver
      )
    }));
    setEditingDriverId(null);
    setEditingDriverName('');
  };

  const handleSaveDriverNotes = () => {
    setRuns(prev => ({
      ...prev,
      drivers: prev.drivers.map(driver =>
        driver.id === editingDriverNotes
          ? { ...driver, notes: editingNotes }
          : driver
      )
    }));
    setEditingDriverNotes(null);
    setEditingNotes('');
  };

  const handleCancelEdit = () => {
    setEditingDriverId(null);
    setEditingDriverNotes(null);
    setEditingDriverName('');
    setEditingNotes('');
  };

  // NOTE: This function is not used since the 'X' on the tile was removed, but it's fine to leave it.
  const handleRemoveFlight = useCallback((flightToRemoveId) => {
    setRuns(prevRuns => {
      let flightToReassign = null;
      
      const updatedDrivers = prevRuns.drivers.map(driver => {
        const flightIndex = driver.flights.findIndex(f => f.uniqueId === flightToRemoveId);
        
        if (flightIndex > -1) {
          flightToReassign = driver.flights[flightIndex];
          return {
            ...driver,
            flights: driver.flights.filter(f => f.uniqueId !== flightToRemoveId),
          };
        }
        return driver;
      });

      if (!flightToReassign) {
        return prevRuns;
      }

      const updatedUnassigned = [...prevRuns.unassigned, flightToReassign].sort((a, b) => {
        const aIsHot = isHotOrPriority(a);
        const bIsHot = isHotOrPriority(b);
        if (aIsHot && !bIsHot) return -1;
        if (!aIsHot && bIsHot) return 1;
        
        // Overnight flights should always sort last
        const aIsOvernight = a.isOvernight || false;
        const bIsOvernight = b.isOvernight || false;
        if (aIsOvernight && !bIsOvernight) return 1;
        if (!aIsOvernight && bIsOvernight) return -1;
        
        return (a.etd || '').localeCompare(b.etd || '');
      });

      return {
        unassigned: updatedUnassigned,
        drivers: updatedDrivers,
      };
    });
  }, [params]);

  // Ghost tile decisions. Confirm accepts the scanned flight into the plan
  // (the tile drops its dashed treatment and behaves like any other);
  // the verify note stays with it for the driver. Dismiss removes the
  // tile. Both update the survival sets AND patch the live runs in place
  // so the change is immediate wherever the tile sits.
  const handleGhostConfirm = useCallback((flightId) => {
    setConfirmedGhostIds((prev) => new Set(prev).add(flightId));
    const confirm = (f) => (f.uniqueId === flightId
      ? { ...f, ghostConfirmed: true }
      : f);
    setRuns((prevRuns) => ({
      unassigned: prevRuns.unassigned.map(confirm),
      drivers: prevRuns.drivers.map((d) => ({
        ...d,
        flights: d.flights.map(confirm),
      })),
    }));
  }, []);

  const handleGhostDismiss = useCallback((flightId) => {
    setDismissedGhostIds((prev) => new Set(prev).add(flightId));
    setRuns((prevRuns) => ({
      unassigned: prevRuns.unassigned.filter((f) => f.uniqueId !== flightId),
      drivers: prevRuns.drivers.map((d) => ({
        ...d,
        flights: d.flights.filter((f) => f.uniqueId !== flightId),
      })),
    }));
  }, []);

  const handleFlightEdit = useCallback((flightId, editedData) => {
    setRuns(prevRuns => {
      const newRuns = {
        unassigned: [...prevRuns.unassigned],
        drivers: prevRuns.drivers.map(d => ({ ...d, flights: [...d.flights] })),
      };

      // Calculate new connection time if ETD changed
      const calculateNewConnectionTime = (flight, newEtd) => {
        if (!newEtd || !inboundETA) return flight.connectionTime;

        // Use the existing calculateConnectionTime function from utils
        const isOvernight = isOvernightFlight(flight);
        return calculateConnectionTime(newEtd, inboundETA, isOvernight);
      };

      // Check unassigned flights
      const unassignedIndex = newRuns.unassigned.findIndex(f => f.uniqueId === flightId);
      if (unassignedIndex > -1) {
        const foundFlight = newRuns.unassigned[unassignedIndex];
        const newConnectionTime = calculateNewConnectionTime(foundFlight, editedData.etd);
        // Use manually set category from edit modal, or calculate if not provided
        const newCategory = editedData.connectionCategory || getConnectionCategory(newConnectionTime, params, foundFlight.isOvernight);

        newRuns.unassigned[unassignedIndex] = {
          ...foundFlight,
          // Update all editable fields
          flightNumber: editedData.flightNumber || foundFlight.flightNumber,
          originalFlightNumber: editedData.flightNumber || foundFlight.originalFlightNumber,
          destination: editedData.destination || foundFlight.destination,
          etd: editedData.etd,
          gate: editedData.gate,
          notes: editedData.notes,
          carousel: editedData.carousel !== undefined ? editedData.carousel : foundFlight.carousel,
          destinations: editedData.destinations || foundFlight.destinations,
          allFreight: editedData.allFreight || foundFlight.allFreight,
          connectionTime: newConnectionTime,
          connectionCategory: newCategory,
          // Saving the edit modal confirms the primary destination, so clear
          // the red "needs attention" pulse on multi-destination tiles.
          primaryAddressed: true
        };
      } else {
        // Check driver flights
        for (const driver of newRuns.drivers) {
          const flightIndex = driver.flights.findIndex(f => f.uniqueId === flightId);
          if (flightIndex > -1) {
            const foundFlight = driver.flights[flightIndex];
            const newConnectionTime = calculateNewConnectionTime(foundFlight, editedData.etd);
            // Use manually set category from edit modal, or calculate if not provided
            const newCategory = editedData.connectionCategory || getConnectionCategory(newConnectionTime, params, foundFlight.isOvernight);

            driver.flights[flightIndex] = {
              ...foundFlight,
              // Update all editable fields
              flightNumber: editedData.flightNumber || foundFlight.flightNumber,
              originalFlightNumber: editedData.flightNumber || foundFlight.originalFlightNumber,
              destination: editedData.destination || foundFlight.destination,
              etd: editedData.etd,
              gate: editedData.gate,
              notes: editedData.notes,
              carousel: editedData.carousel !== undefined ? editedData.carousel : foundFlight.carousel,
              destinations: editedData.destinations || foundFlight.destinations,
              allFreight: editedData.allFreight || foundFlight.allFreight,
              connectionTime: newConnectionTime,
              connectionCategory: newCategory,
              primaryAddressed: true
            };
            break;
          }
        }
      }

      return newRuns;
    });
  }, [params, inboundETA]);

  const handleSplitLocal = useCallback((driverId) => {
    setRuns(prevRuns => {
      const driver = prevRuns.drivers.find(d => d.id === driverId);
      if (!driver || !driver.isLocalDriver || driver.flights.length === 0) return prevRuns;
      
      const localFlight = driver.flights[0];
      if (!localFlight || !localFlight.isLocal) return prevRuns;
      
      // Check if this is already a split driver (FWD/AFT in name)
      const isAlreadySplit = driver.name.includes('FWD') || driver.name.includes('AFT');
      
      if (isAlreadySplit) {
        // For already split drivers, just split the bag count in half
        const currentBags = localFlight.bags || 0;
        if (currentBags <= 1) return prevRuns; // Can't split 1 or fewer bags
        
        const firstHalf = Math.ceil(currentBags / 2);
        const secondHalf = currentBags - firstHalf;
        
        // Generate unique numbers for the split
        const splitNum = Date.now();
        const driverBaseName = driver.name.replace(/\s+\d+$/, ''); // Remove any trailing numbers
        
        // Create new drivers
        const newDrivers = prevRuns.drivers.filter(d => d.id !== driverId);
        const baseId = Math.max(...prevRuns.drivers.map(d => d.id || 0)) + 1;
        
        // First split driver
        newDrivers.push({
          id: baseId,
          name: `${driverBaseName} 1`,
          notes: flightNote || '',
          isLocalDriver: true,
          flights: [{
            ...localFlight,
            uniqueId: `local-split1-${splitNum}`,
            bags: firstHalf,
            binDistribution: localFlight.binDistribution // Keep same bin distribution for display
          }]
        });
        
        // Second split driver
        newDrivers.push({
          id: baseId + 1,
          name: `${driverBaseName} 2`,
          notes: flightNote || '',
          isLocalDriver: true,
          flights: [{
            ...localFlight,
            uniqueId: `local-split2-${splitNum}`,
            bags: secondHalf,
            binDistribution: localFlight.binDistribution // Keep same bin distribution for display
          }]
        });
        
        return {
          ...prevRuns,
          drivers: newDrivers
        };
      } else {
        // Original FWD/AFT split logic for non-split drivers
        const fwdBins = ['A', 'B', 'C'];
        const aftBins = ['D', 'E', 'F'];
        let fwdBags = 0;
        let aftBags = 0;
        const fwdDistribution = {};
        const aftDistribution = {};
        
        if (localFlight.binDistribution) {
          Object.entries(localFlight.binDistribution).forEach(([bin, count]) => {
            if (fwdBins.includes(bin)) {
              fwdBags += count;
              fwdDistribution[bin] = count;
            } else if (aftBins.includes(bin)) {
              aftBags += count;
              aftDistribution[bin] = count;
            }
          });
        }
        
        // Only split if we have bags in both compartments
        if (fwdBags === 0 || aftBags === 0) return prevRuns;
        
        // Create new FWD and AFT local drivers
        const newDrivers = prevRuns.drivers.filter(d => d.id !== driverId);
        const baseId = Math.max(...prevRuns.drivers.map(d => d.id || 0)) + 1;
        
        // FWD Local Driver
        newDrivers.push({
          id: baseId,
          name: 'Local Driver FWD',
          notes: flightNote || '',
          isLocalDriver: true,
          flights: [{
            ...localFlight,
            uniqueId: `local-fwd-${Date.now()}`,
            bags: fwdBags,
            binDistribution: fwdDistribution
          }]
        });
        
        // AFT Local Driver
        newDrivers.push({
          id: baseId + 1,
          name: 'Local Driver AFT',
          notes: flightNote || '',
          isLocalDriver: true,
          flights: [{
            ...localFlight,
            uniqueId: `local-aft-${Date.now()}`,
            bags: aftBags,
            binDistribution: aftDistribution
          }]
        });
        
        return {
          ...prevRuns,
          drivers: newDrivers
        };
      }
    });
  }, []);

  const handlePinClick = useCallback((flightId) => {
    setRuns(prevRuns => {
      const newRuns = {
        unassigned: [...prevRuns.unassigned],
        drivers: prevRuns.drivers.map(d => ({ ...d, flights: [...d.flights] })),
      };

      // Find which driver contains this flight
      let targetDriver = null;
      let targetFlightIndex = -1;
      
      for (const driver of newRuns.drivers) {
        const index = driver.flights.findIndex(f => f.uniqueId === flightId);
        if (index > -1) {
          targetDriver = driver;
          targetFlightIndex = index;
          break;
        }
      }

      if (!targetDriver || targetFlightIndex === -1) return prevRuns;

      // Get current pin positions in this driver's run
      const currentPins = {
        1: targetDriver.flights.find(f => f.pinPosition === 1),
        2: targetDriver.flights.find(f => f.pinPosition === 2),
        3: targetDriver.flights.find(f => f.pinPosition === 3)
      };

      const currentFlight = targetDriver.flights[targetFlightIndex];
      const currentPin = currentFlight.pinPosition || 0;

      // Determine next available position
      let nextPin = 0;
      if (currentPin === 0) {
        // Find first available position
        for (let i = 1; i <= 3; i++) {
          if (!currentPins[i] || currentPins[i].uniqueId === flightId) {
            nextPin = i;
            break;
          }
        }
      } else {
        // Cycle to next available position
        for (let i = currentPin + 1; i <= 3; i++) {
          if (!currentPins[i]) {
            nextPin = i;
            break;
          }
        }
        // If no higher position available, cycle back to unpinned
        if (nextPin === 0 && currentPin < 3) {
          nextPin = 0;
        }
      }

      // Update the flight's pin position
      targetDriver.flights[targetFlightIndex] = {
        ...currentFlight,
        pinPosition: nextPin
      };

      // Sort flights: pinned first (1, 2, 3), then by ETD
      targetDriver.flights.sort((a, b) => {
        // Pinned flights come first
        if (a.pinPosition && !b.pinPosition) return -1;
        if (!a.pinPosition && b.pinPosition) return 1;
        
        // Among pinned flights, sort by pin position
        if (a.pinPosition && b.pinPosition) {
          return a.pinPosition - b.pinPosition;
        }
        
        // Among unpinned flights, sort by ETD treating early morning as late night
        const parseETDForSort = (etd) => {
          if (!etd) return 0;
          const [hours, minutes] = etd.replace(/[LZ]/gi, '').split(':').map(n => Number(n) || 0);
          // If it's early morning (midnight to 5am), add 24 hours for sorting
          if (hours < 5) {
            return (hours + 24) * 60 + minutes;
          }
          return hours * 60 + minutes;
        };
        
        const aMinutes = parseETDForSort(a.etd);
        const bMinutes = parseETDForSort(b.etd);
        return aMinutes - bMinutes;
      });

      return newRuns;
    });
  }, []);

  // RCS thruster quads: while a tile is dragged, decompose the motion
  // vector into per-side burn intensities — moving right fires the WEST
  // jet (thrust opposes motion), diagonals fire two jets at once.
  // Written straight to CSS vars on the overlay node — no React state, so
  // pointer-rate updates cost nothing. Themes opt in via the vars
  // (Apollo's flames read --rcs-n / --rcs-s / --rcs-e / --rcs-w).
  const rcsRef = useRef({ prevDelta: null, jets: { n: 0, s: 0, e: 0, w: 0 }, fadeTimer: null });

  const handleDragStart = useCallback((event) => {
    const { active } = event;
    const allFlights = [...runs.unassigned, ...runs.drivers.flatMap(d => d.flights)];
    const flight = allFlights.find(f => f.uniqueId === active.id);
    rcsRef.current.prevDelta = null;
    setActiveFlight(flight);
  }, [runs]);

  const handleDragMove = useCallback((event) => {
    const r = rcsRef.current;
    const { delta } = event;
    const prev = r.prevDelta;
    r.prevDelta = { x: delta.x, y: delta.y };
    if (!prev) return;
    const node = document.querySelector('.flight-tile--drag-overlay');
    if (!node) return;
    const mx = delta.x - prev.x;
    const my = delta.y - prev.y;
    const mag = Math.hypot(mx, my);
    if (mag < 1) return;
    // exhaust appears on the trailing side: moving right → west jet, etc.
    const target = {
      w: Math.max(0, mx / mag),
      e: Math.max(0, -mx / mag),
      n: Math.max(0, my / mag),
      s: Math.max(0, -my / mag),
    };
    for (const side of ['n', 's', 'e', 'w']) {
      r.jets[side] += (target[side] - r.jets[side]) * 0.45; // smooth
      node.style.setProperty(`--rcs-${side}`, r.jets[side].toFixed(2));
    }
    // burns cut when the hand stops
    if (r.fadeTimer) clearTimeout(r.fadeTimer);
    r.fadeTimer = setTimeout(() => {
      for (const side of ['n', 's', 'e', 'w']) {
        r.jets[side] = 0;
        node.style.setProperty(`--rcs-${side}`, '0');
      }
    }, 140);
  }, []);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    setActiveFlight(null);

    if (!over) return;
    const activeId = active.id;
    const overId = over.id;
    const sourceColumnId = active.data.current?.sourceColumnId;

    // Detect tile-on-tile drop (for within-driver reordering)
    const isTileDrop = typeof overId === 'string' && overId.startsWith('tile-drop-');
    const targetFlightId = isTileDrop ? overId.replace('tile-drop-', '') : null;
    const targetColumnId = isTileDrop ? over.data.current?.sourceColumnId : overId;

    // Same tile → no-op
    if (isTileDrop && targetFlightId === activeId) return;
    // Same column, not a tile drop → no-op
    if (!isTileDrop && sourceColumnId === overId) return;

    // Within-driver reorder: tile dropped on another tile in the same driver
    if (isTileDrop && sourceColumnId === targetColumnId && sourceColumnId !== 'unassigned') {
      setRuns(prev => {
        const newRuns = {
          unassigned: [...prev.unassigned],
          drivers: prev.drivers.map(d => ({ ...d, flights: [...d.flights] })),
        };

        const driver = newRuns.drivers.find(d => `driver-${d.id}` === sourceColumnId);
        if (!driver) return prev;

        const fromIndex = driver.flights.findIndex(f => f.uniqueId === activeId);
        const toIndex = driver.flights.findIndex(f => f.uniqueId === targetFlightId);
        if (fromIndex === -1 || toIndex === -1) return prev;

        // Move tile from old position to new position
        const [moved] = driver.flights.splice(fromIndex, 1);
        driver.flights.splice(toIndex, 0, moved);

        // Set runOrder on all flights and clear pin positions
        driver.flights = driver.flights.map((f, i) => {
          const updated = { ...f, runOrder: i + 1 };
          delete updated.pinPosition;
          return updated;
        });

        // Add "Run in this order please." to driver notes
        if (!driver.notes?.includes('Run in this order please.')) {
          driver.notes = driver.notes
            ? `${driver.notes} | Run in this order please.`
            : 'Run in this order please.';
        }

        return newRuns;
      });
      return;
    }

    // Cross-column move
    setRuns(prev => {
      let sourceList;
      if (sourceColumnId === 'unassigned') {
        sourceList = prev.unassigned;
      } else {
        sourceList = prev.drivers.find(d => `driver-${d.id}` === sourceColumnId)?.flights;
      }

      const flightIndex = sourceList?.findIndex(f => f.uniqueId === activeId);
      const movedFlight = flightIndex > -1 ? sourceList[flightIndex] : null;

      if (!movedFlight) return prev;

      const newRuns = {
        unassigned: [...prev.unassigned],
        drivers: prev.drivers.map(d => ({ ...d, flights: [...d.flights] })),
      };

      // Remove from source
      if (sourceColumnId === 'unassigned') {
        newRuns.unassigned = newRuns.unassigned.filter(f => f.uniqueId !== activeId);
      } else {
        const srcDriver = newRuns.drivers.find(d => `driver-${d.id}` === sourceColumnId);
        if (srcDriver) {
          srcDriver.flights = srcDriver.flights.filter(f => f.uniqueId !== activeId);
          // Renumber if source driver was in "run in this order" mode
          if (srcDriver.flights.some(f => f.runOrder)) {
            srcDriver.flights = srcDriver.flights.map((f, i) => ({ ...f, runOrder: i + 1 }));
          }
        }
      }

      // Prepare the flight for the target (clear positional data)
      const cleanedFlight = { ...movedFlight };
      delete cleanedFlight.pinPosition;
      delete cleanedFlight.runOrder;

      // Add to target
      const resolvedTarget = targetColumnId || overId;
      if (resolvedTarget === 'unassigned') {
        newRuns.unassigned.push(cleanedFlight);
        newRuns.unassigned.sort((a, b) => {
            const aIsHot = isHotOrPriority(a);
            const bIsHot = isHotOrPriority(b);
            if (aIsHot && !bIsHot) return -1;
            if (!aIsHot && bIsHot) return 1;

            const aIsOvernight = a.isOvernight || false;
            const bIsOvernight = b.isOvernight || false;
            if (aIsOvernight && !bIsOvernight) return 1;
            if (!aIsOvernight && bIsOvernight) return -1;

            return (a.etd || '').localeCompare(b.etd || '');
        });
      } else {
        const destDriver = newRuns.drivers.find(d => `driver-${d.id}` === resolvedTarget);
        if (destDriver) {
            const hasRunOrder = destDriver.flights.some(f => f.runOrder);
            if (hasRunOrder) {
              // Target driver is in "run in this order" mode — add at end
              cleanedFlight.runOrder = destDriver.flights.length + 1;
              destDriver.flights.push(cleanedFlight);
            } else {
              // Normal mode — add and sort by pin position then ETD
              destDriver.flights.push(cleanedFlight);
              destDriver.flights.sort((a, b) => {
                  if (a.pinPosition && !b.pinPosition) return -1;
                  if (!a.pinPosition && b.pinPosition) return 1;
                  if (a.pinPosition && b.pinPosition) {
                      return a.pinPosition - b.pinPosition;
                  }
                  const parseETDForSort = (etd) => {
                    if (!etd) return 0;
                    const [hours, minutes] = etd.replace(/[LZ]/gi, '').split(':').map(n => Number(n) || 0);
                    if (hours < 5) {
                      return (hours + 24) * 60 + minutes;
                    }
                    return hours * 60 + minutes;
                  };

                  const aMinutes = parseETDForSort(a.etd);
                  const bMinutes = parseETDForSort(b.etd);
                  return aMinutes - bMinutes;
              });
            }
        }
      }

      return newRuns;
    });
  }, [params]);

  const handleAutoAssign = useCallback((assignType) => {
    if (assignType === 'UNASSIGN_ALL') {
      setSplitMode(false);
      setAddMode(false);
      setSelectedDriverId(null);
      setActiveFlight(null);
      setEditingDriverId(null);
      setEditingDriverNotes(null);
      setEditingDriverName('');
      setEditingNotes('');
      // Full reset brings dismissed ghosts back and clears confirmations —
      // "send every tile back and start over" includes the ghost decisions.
      setConfirmedGhostIds(new Set());
      setDismissedGhostIds(new Set());
      if (initialRunsRef.current) {
        setRuns(() => cloneRuns(initialRunsRef.current));
        return;
      }
    }

    setRuns(prev => {
      // Unconfirmed ghosts never join automated runs — hold them aside and
      // re-append to whatever Unassigned each branch returns.
      const ghostHold = prev.unassigned.filter(f => f.isGhost && !f.ghostConfirmed);
      let allUnassigned = prev.unassigned.filter(f => !(f.isGhost && !f.ghostConfirmed));
      // Separate local and non-local drivers from the start
      const localDrivers = prev.drivers.filter(d => d.isLocalDriver);
      let currentDrivers = prev.drivers.filter(d => !d.isLocalDriver);
      
      // Helper function to get gate number
      const getGateNum = (flight) => {
        if (!flight.gate) return null;
        const match = flight.gate.match(/(\d+)/);
        return match ? parseInt(match[1], 10) : null;
      };
      
      if (assignType === 'UNASSIGN_ALL') {
        // currentDrivers already excludes local drivers, so just use it directly
        const allDriverFlights = currentDrivers.flatMap(d => d.flights);
        
        // Need to reconstruct original flights from split flights
        const reconstructedFlights = new Map();
        
        allDriverFlights.forEach(flight => {
          if (flight.isSplit && flight.originalId) {
            // This is a split flight, we need to reconstruct the original
            if (!reconstructedFlights.has(flight.originalId)) {
              // Check if we have stored original flight data (for freight splits)
              if (flight.originalFlightData) {
                // Perfect reconstruction from stored original data
                const originalFlight = JSON.parse(flight.originalFlightData);
                reconstructedFlights.set(flight.originalId, originalFlight);
              } else {
                // Fallback for FWD/AFT splits that don't have stored original data
                const bagTile = allDriverFlights.find(f => f.originalId === flight.originalId && !f.isFreightOnly);
                const freightTile = allDriverFlights.find(f => f.originalId === flight.originalId && f.isFreightOnly);
                
                // Start with the bag tile as base (it has the original structure)
                const baseFlight = bagTile ? { ...bagTile } : { ...flight };
                
                // Clean up split properties
                delete baseFlight.splitType;
                delete baseFlight.isSplit;
                delete baseFlight.isFreightOnly;
                if (baseFlight.connectionCategory === 'freight-only') {
                  delete baseFlight.connectionCategory;
                }
                baseFlight.uniqueId = flight.originalId;
                delete baseFlight.originalId;
                delete baseFlight.originalFlightData;
                
                // No longer need to restore destination name since we don't add "(Freight)" suffix
                
                // If we have a freight tile, restore freight data from it
                if (freightTile) {
                  baseFlight.allFreight = freightTile.allFreight || [];
                  baseFlight.freightByBin = freightTile.freightByBin || {};
                  
                  // Restore destination freight data
                  if (freightTile.destinations && baseFlight.destinations) {
                    baseFlight.destinations = baseFlight.destinations.map((dest, idx) => ({
                      ...dest,
                      freight: freightTile.destinations[idx]?.freight || []
                    }));
                  }
                }
                
                reconstructedFlights.set(flight.originalId, baseFlight);

                // For FWD/AFT splits, we need to combine bags from both compartments
                // But NOT for auto-split bag/freight tiles (isColdMixedSplit)
                // For auto-splits, the reconstruction above already has the correct data
                if (flight.splitType && !flight.isColdMixedSplit) {
                  // This is a FWD/AFT split - need to find and combine both tiles
                  const fwdTile = allDriverFlights.find(f => f.originalId === flight.originalId && f.splitType === 'FWD');
                  const aftTile = allDriverFlights.find(f => f.originalId === flight.originalId && f.splitType === 'AFT');

                  const existingFlight = reconstructedFlights.get(flight.originalId);

                  // Start with bags from one tile (already in baseFlight)
                  let totalBags = 0;
                  const combinedBinDistribution = {};

                  // Add bags from both tiles
                  if (fwdTile) {
                    totalBags += (fwdTile.bags || 0);
                    if (fwdTile.binDistribution) {
                      Object.entries(fwdTile.binDistribution).forEach(([bin, count]) => {
                        combinedBinDistribution[bin] = count;
                      });
                    }
                  }
                  if (aftTile) {
                    totalBags += (aftTile.bags || 0);
                    if (aftTile.binDistribution) {
                      Object.entries(aftTile.binDistribution).forEach(([bin, count]) => {
                        combinedBinDistribution[bin] = count;
                      });
                    }
                  }

                  existingFlight.bags = totalBags;
                  existingFlight.binDistribution = combinedBinDistribution;
                }
                // For auto-splits (isColdMixedSplit), the reconstruction is already complete
                // No need to add bags - they're already correct from the bagTile
              }
            }
          } else {
            // Non-split flight, add as-is
            reconstructedFlights.set(flight.uniqueId, flight);
          }
        });
        
        // Filter out split flights from unassigned since they're being reconstructed
        const nonSplitUnassigned = allUnassigned.filter(flight => !flight.isSplit);
        
        const newUnassigned = [...nonSplitUnassigned, ...Array.from(reconstructedFlights.values())].sort((a, b) => {
            const aIsHot = isHotOrPriority(a);
            const bIsHot = isHotOrPriority(b);
            if (aIsHot && !bIsHot) return -1;
            if (!aIsHot && bIsHot) return 1;
            
            const aIsOvernight = a.isOvernight || false;
            const bIsOvernight = b.isOvernight || false;
            if (aIsOvernight && !bIsOvernight) return 1;
            if (!aIsOvernight && bIsOvernight) return -1;
            
            return (a.etd || '').localeCompare(b.etd || '');
        });
        // Also exit split mode when resetting all
        setSplitMode(false);
        return { drivers: localDrivers, unassigned: [...newUnassigned, ...ghostHold] };
      }

      const createDriversFromChunks = (chunks, runNamePrefix) => {
        let nextDriverId = (currentDrivers.length > 0) ? Math.max(...currentDrivers.map(d => d.id)) + 1 : 1;
        // For auto-assignment, always start numbering from 1
        let driverCounter = 1;
        chunks.forEach((chunk, index) => {
          const driverName = `Driver ${driverCounter++}`;
          const newDriver = { id: nextDriverId++, name: driverName, notes: flightNote || '', flights: chunk };
          currentDrivers.unshift(newDriver);
        });
      };
      
      if (assignType === 'HOT' || assignType === 'PRIORITY') {
        const categoryToTarget = assignType === 'HOT' ? 'hot' : 'priority';
        const stopLimit = assignType === 'HOT' ? 3 : 6;
        const runName = assignType === 'HOT' ? 'Hot Bag Run' : 'Priority Run';
        const targetFlights = allUnassigned
          .filter(f => f.connectionCategory === categoryToTarget)
          .sort((a, b) => (a.etd || '').localeCompare(b.etd || ''));
        if (targetFlights.length === 0) return prev;
        const remainingFlights = allUnassigned.filter(f => !targetFlights.some(t => t.uniqueId === f.uniqueId));
        const chunks = [];
        for (let i = 0; i < targetFlights.length; i += stopLimit) {
            chunks.push(targetFlights.slice(i, i + stopLimit));
        }
        createDriversFromChunks(chunks, runName);
        return { unassigned: [...remainingFlights, ...ghostHold], drivers: [...currentDrivers, ...localDrivers] };
      }

      if (['ODDS_EVENS', 'EAST_WEST', 'SECTORS', 'QUADRANTS', 'COLD', 'SOLO'].includes(assignType)) {
        let breakdowns = {};
        if (assignType === 'COLD') breakdowns['Cold Run'] = allUnassigned.filter(f => f.connectionCategory === 'cold');
        if (assignType === 'SOLO') {
          if (splitMode) {
            // Create separate FWD and AFT solo runs when in split mode
            breakdowns['Solo FWD'] = allUnassigned.filter(f => f.splitType === 'FWD');
            breakdowns['Solo AFT'] = allUnassigned.filter(f => f.splitType === 'AFT');
          } else {
            breakdowns['Solo Run'] = allUnassigned;
          }
        }
        if (assignType === 'ODDS_EVENS') {
          const oddsFlights = allUnassigned.filter(f => getGateNum(f) % 2 !== 0);
          const evensFlights = allUnassigned.filter(f => getGateNum(f) % 2 === 0);
          
          if (splitMode) {
            // Create FWD/AFT combinations for odds and evens
            breakdowns['Odds FWD'] = oddsFlights.filter(f => f.splitType === 'FWD' || (!f.isSplit && !f.splitType));
            breakdowns['Odds AFT'] = oddsFlights.filter(f => f.splitType === 'AFT');
            breakdowns['Evens FWD'] = evensFlights.filter(f => f.splitType === 'FWD' || (!f.isSplit && !f.splitType));
            breakdowns['Evens AFT'] = evensFlights.filter(f => f.splitType === 'AFT');
          } else {
            breakdowns['Odds (North)'] = oddsFlights;
            breakdowns['Evens (South)'] = evensFlights;
          }
        }
        if (assignType === 'EAST_WEST') {
          const westFlights = allUnassigned.filter(f => getGateNum(f) <= 47);
          const eastFlights = allUnassigned.filter(f => getGateNum(f) >= 48);
          
          if (splitMode) {
            // Create FWD/AFT combinations for east and west
            breakdowns['West FWD'] = westFlights.filter(f => f.splitType === 'FWD');
            breakdowns['West AFT'] = westFlights.filter(f => f.splitType === 'AFT');
            breakdowns['East FWD'] = eastFlights.filter(f => f.splitType === 'FWD');
            breakdowns['East AFT'] = eastFlights.filter(f => f.splitType === 'AFT');
          } else {
            breakdowns['West Run (C23-C47)'] = westFlights;
            breakdowns['East Run (C48-C67)'] = eastFlights;
          }
        }
        if (assignType === 'SECTORS') {
          const sector1Flights = allUnassigned.filter(f => sectors.sector1.includes(getGateNum(f)));
          const sector2Flights = allUnassigned.filter(f => sectors.sector2.includes(getGateNum(f)));
          const sector3Flights = allUnassigned.filter(f => sectors.sector3.includes(getGateNum(f)));
          
          if (splitMode) {
            // Create FWD/AFT combinations for each sector
            breakdowns['Sector 1 FWD'] = sector1Flights.filter(f => f.splitType === 'FWD');
            breakdowns['Sector 1 AFT'] = sector1Flights.filter(f => f.splitType === 'AFT');
            breakdowns['Sector 2 FWD'] = sector2Flights.filter(f => f.splitType === 'FWD');
            breakdowns['Sector 2 AFT'] = sector2Flights.filter(f => f.splitType === 'AFT');
            breakdowns['Sector 3 FWD'] = sector3Flights.filter(f => f.splitType === 'FWD');
            breakdowns['Sector 3 AFT'] = sector3Flights.filter(f => f.splitType === 'AFT');
          } else {
            breakdowns['Sector 1 (23-39)'] = sector1Flights;
            breakdowns['Sector 2 (40-55)'] = sector2Flights;
            breakdowns['Sector 3 (56-67)'] = sector3Flights;
          }
        }
        if (assignType === 'QUADRANTS') {
          const nwFlights = allUnassigned.filter(f => quadrants.nw.includes(getGateNum(f)));
          const swFlights = allUnassigned.filter(f => quadrants.sw.includes(getGateNum(f)));
          const neFlights = allUnassigned.filter(f => quadrants.ne.includes(getGateNum(f)));
          const seFlights = allUnassigned.filter(f => quadrants.se.includes(getGateNum(f)));
          
          if (splitMode) {
            // Create FWD/AFT combinations for each quadrant
            breakdowns['NW FWD'] = nwFlights.filter(f => f.splitType === 'FWD');
            breakdowns['NW AFT'] = nwFlights.filter(f => f.splitType === 'AFT');
            breakdowns['SW FWD'] = swFlights.filter(f => f.splitType === 'FWD');
            breakdowns['SW AFT'] = swFlights.filter(f => f.splitType === 'AFT');
            breakdowns['NE FWD'] = neFlights.filter(f => f.splitType === 'FWD');
            breakdowns['NE AFT'] = neFlights.filter(f => f.splitType === 'AFT');
            breakdowns['SE FWD'] = seFlights.filter(f => f.splitType === 'FWD');
            breakdowns['SE AFT'] = seFlights.filter(f => f.splitType === 'AFT');
          } else {
            breakdowns['North West'] = nwFlights;
            breakdowns['South West'] = swFlights;
            breakdowns['North East'] = neFlights;
            breakdowns['South East'] = seFlights;
          }
        }
        const allAssignedIds = new Set(Object.values(breakdowns).flat().map(f => f.uniqueId));
        const remaining = allUnassigned.filter(f => !allAssignedIds.has(f.uniqueId));
        let nextDriverId = (currentDrivers.length > 0) ? Math.max(...currentDrivers.map(d => d.id)) + 1 : 1;
        let driverCounter = currentDrivers.length + 1;
        for (const [name, flights] of Object.entries(breakdowns)) {
          if (flights.length > 0) {
            const driverName = `Driver ${driverCounter++}`;
            currentDrivers.unshift({
              id: nextDriverId++,
              name: driverName,
              notes: flightNote || '',
              flights: flights.sort((a, b) => {
                // Sort by ETD treating early morning as late night
                const parseETDForSort = (etd) => {
                  if (!etd) return 0;
                  const [hours, minutes] = etd.replace(/[LZ]/gi, '').split(':').map(n => Number(n) || 0);
                  // If it's early morning (midnight to 5am), add 24 hours for sorting
                  if (hours < 5) {
                    return (hours + 24) * 60 + minutes;
                  }
                  return hours * 60 + minutes;
                };
                
                const aMinutes = parseETDForSort(a.etd);
                const bMinutes = parseETDForSort(b.etd);
                return aMinutes - bMinutes;
              })
            });
          }
        }
        return { unassigned: [...remaining, ...ghostHold], drivers: [...currentDrivers, ...localDrivers] };
      }
      
      if (['20s', '30s', '40s', '50s', '60s'].includes(assignType)) {
        const gateRange = assignType;
        const rangeMin = parseInt(gateRange) || 0;
        const rangeMax = rangeMin + 9;
        
        const gateFlights = allUnassigned.filter(f => {
          const gateNum = getGateNum(f);
          return gateNum && gateNum >= rangeMin && gateNum <= rangeMax;
        });

        if (gateFlights.length === 0) return prev;

        if (addMode && selectedDriverId) {
          const driverIndex = currentDrivers.findIndex(d => d.id === selectedDriverId);
          if (driverIndex !== -1) {
            currentDrivers[driverIndex].flights = [...currentDrivers[driverIndex].flights, ...gateFlights]
              .sort((a, b) => (a.etd || '').localeCompare(b.etd || ''));
            const remaining = allUnassigned.filter(f => !gateFlights.some(g => g.uniqueId === f.uniqueId));
            return { drivers: [...currentDrivers, ...localDrivers], unassigned: [...remaining, ...ghostHold] };
          }
        } else {
          const remaining = allUnassigned.filter(f => !gateFlights.some(g => g.uniqueId === f.uniqueId));
          const chunks = [];
          for (let i = 0; i < gateFlights.length; i += 6) {
              chunks.push(gateFlights.slice(i, i + 6));
          }
          createDriversFromChunks(chunks, `${gateRange} Gates`);
          return { drivers: [...currentDrivers, ...localDrivers], unassigned: [...remaining, ...ghostHold] };
        }
      }
      
      if (assignType === 'SPLIT_ALL_FREIGHT') {
        const newlySplit = [];
        const alreadyFine = [];
        allUnassigned.forEach(f => {
          if ((f.bags > 0) && (f.allFreight.length > 0) && !f.isSplit) {
            // Store the original flight data for perfect reconstruction
            const originalFlightData = JSON.stringify(f);
            
            // Create bag-only tile - remove ALL freight data
            const bagTile = { 
              ...f, 
              uniqueId: `${f.uniqueId}-bags`, 
              originalId: f.uniqueId,
              originalFlightData, // Store original for perfect reset
              isSplit: true, 
              allFreight: [], 
              freightByBin: {},
              // Clear destinations freight data
              destinations: f.destinations ? f.destinations.map(dest => ({
                ...dest,
                freight: []
              })) : undefined
            };
            newlySplit.push(bagTile);
            
            // Create freight-only tile
            const freightTile = { 
              ...f, 
              uniqueId: `${f.uniqueId}-freight`, 
              originalId: f.uniqueId, 
              originalFlightData, // Store original for perfect reset
              isSplit: true, 
              isFreightOnly: true, 
              connectionCategory: 'freight-only', 
              bags: 0, 
              binDistribution: {},
              destination: f.destination,
              // Preserve multi-destination structure and clear bag data from destinations but keep freight
              isMultiDestination: f.isMultiDestination, // Preserve this flag
              destinations: f.destinations ? f.destinations.map(dest => ({
                ...dest,
                bags: 0  // Clear bags from all destinations on freight tile
              })) : undefined
            };
            newlySplit.push(freightTile);
          } else {
            alreadyFine.push(f);
          }
        });
        const finalUnassigned = [...alreadyFine, ...newlySplit].sort((a, b) => {
            const aIsHot = isHotOrPriority(a);
            const bIsHot = isHotOrPriority(b);
            if (aIsHot && !bIsHot) return -1;
            if (!aIsHot && bIsHot) return 1;
            return (a.etd || '').localeCompare(b.etd || '');
        });
        return { ...prev, unassigned: [...finalUnassigned, ...ghostHold] };
      }
      
      if (assignType === 'ASSIGN_FREIGHT_ONLY') {
        const freightOnlyFlights = allUnassigned.filter(f => (f.bags === 0) && (f.allFreight.length > 0));
        const remaining = allUnassigned.filter(f => !freightOnlyFlights.some(t => t.uniqueId === f.uniqueId));
        if (freightOnlyFlights.length > 0) {
            createDriversFromChunks([freightOnlyFlights.sort((a,b) => (a.etd||'').localeCompare(b.etd||''))], 'Freight Run');
        }
        // currentDrivers was already filtered to exclude local drivers at line 1258
        // localDrivers still has the local drivers preserved from line 1257
        return { drivers: [...currentDrivers, ...localDrivers], unassigned: [...remaining, ...ghostHold] };
      }
      
      if (assignType === 'COMBINE_ALL_FREIGHT') {
          const combinedFlightsMap = new Map();
          const remainingFlights = [];

          allUnassigned.forEach(flight => {
              if (flight.isSplit && flight.originalId) {
                  const baseId = flight.originalId;
                  if (!combinedFlightsMap.has(baseId)) {
                      // Use stored original flight data for perfect reconstruction
                      if (flight.originalFlightData) {
                          const originalFlight = JSON.parse(flight.originalFlightData);
                          combinedFlightsMap.set(baseId, originalFlight);
                      } else {
                          // Fallback for splits without stored original data
                          combinedFlightsMap.set(baseId, { ...flight });
                          const combinedFlight = combinedFlightsMap.get(baseId);
                          
                          // Only collect bags and freight from their respective runs
                          if (flight.bags > 0) {
                              // This is the bag run - use its bag data
                              combinedFlight.bags = flight.bags;
                              combinedFlight.binDistribution = { ...flight.binDistribution };
                          }
                          if (flight.allFreight && flight.allFreight.length > 0) {
                              // This is the freight run - use its freight data  
                              combinedFlight.allFreight = [...flight.allFreight];
                              combinedFlight.freightByBin = { ...flight.freightByBin };
                          }
                      }
                  }
              } else {
                  remainingFlights.push(flight);
              }
          });

          if (combinedFlightsMap.size === 0) return prev;

          const mergedList = Array.from(combinedFlightsMap.values()).map(f => ({
              ...f,
              isSplit: false,
              // Only clean up freight destination if it has the suffix
              destination: f.destination,
              uniqueId: f.uniqueId || (f.originalId + '-combined-' + Date.now())
          }));
          
          const finalUnassigned = [...remainingFlights, ...mergedList].sort((a, b) => {
            const aIsHot = isHotOrPriority(a);
            const bIsHot = isHotOrPriority(b);
            if (aIsHot && !bIsHot) return -1;
            if (!aIsHot && bIsHot) return 1;
            return (a.etd || '').localeCompare(b.etd || '');
          });

          return { ...prev, unassigned: [...finalUnassigned, ...ghostHold] };
      }

      return prev;
    });
  }, [params, brockData, tdrData, addMode, selectedDriverId, splitMode]);

  // Capture the inbound header strip + the full board grid, stitch them onto
  // one image, and write it to the clipboard so the dispatcher can paste
  // directly into Teams. We put both text/html (containing the label + the
  // image inline as a base64 data URL) and image/png on the clipboard.
  // Teams parses the text/html, rendering the label as searchable text and
  // the image inline — same as if the dispatcher had selected a webpage
  // section with text + image and hit ⌘C. The async work is wrapped in
  // Promise<Blob> values inside ClipboardItem so Safari's user-gesture
  // chain stays intact across `await`.
  // Every undecided ghost on the board, wherever it sits.
  const getUnresolvedGhosts = useCallback(() => ([
    ...runs.unassigned,
    ...runs.drivers.flatMap((d) => d.flights),
  ].filter((f) => f.isGhost && !f.ghostConfirmed)), [runs]);

  const triggerPrint = useCallback(() => {
    const hiddenButton = document.querySelector('[data-print-dispatch="true"] button');
    if (hiddenButton) {
      hiddenButton.click();
    }
  }, []);

  const handleShareToTeams = useCallback(() => {
    if (shareStatus === 'busy') return;
    const headerEl = headerCaptureRef.current;
    const boardEl = boardGridRef.current;
    if (!headerEl || !boardEl) return;

    setShareStatus('busy');

    // Build the "{flightNumber} {arrivingFrom} {arrivalGate}" label, e.g. "352 BNA C26".
    const flightNum = tdrData?.inbound?.flightNumber || '';
    const arrivingFrom = tdrData?.inbound?.arrivingFrom || '';
    const arrivalGate = tdrData?.inbound?.arrivalGate || '';
    const label = `${flightNum} ${arrivingFrom} ${arrivalGate}`.trim() || 'Dispatch board';
    const escapedLabel = label
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    const blobPromise = (async () => {
      const [headerCanvas, boardCanvas] = await Promise.all([
        html2canvas(headerEl, { backgroundColor: null, scale: 2, useCORS: true }),
        html2canvas(boardEl, {
          backgroundColor: null,
          scale: 2,
          useCORS: true,
          onclone: (_doc, clonedGrid) => {
            // Theme reveal animations (the redaction bars that wipe off the
            // column titles on blackbird/overwatch) restart inside the capture
            // clone, so the snapshot catches the bars still covering the
            // titles. html2canvas inlines ::after pseudos as real child
            // elements during cloning, so kill the covers by removing those
            // nodes — the bars are invisible at rest and contribute nothing.
            _doc.querySelectorAll("[class*='pseudoelement_after']").forEach((el) => {
              const host = el.parentElement;
              if (host && (host.classList.contains('board-column-head') ||
                           host.classList.contains('board-inbound-strip'))) {
                el.remove();
              }
            });
            // Remove the unassigned column from the captured copy and re-flow
            // the grid template so the remaining driver columns sit flush left.
            const excluded = clonedGrid.querySelectorAll('[data-share-exclude="true"]');
            excluded.forEach((el) => el.remove());
            const remaining = clonedGrid.children.length;
            clonedGrid.style.gridTemplateColumns = remaining > 0
              ? `repeat(${remaining}, 320px)`
              : '320px';
          },
        }),
      ]);

      const gap = 32;
      const padding = 48;
      const width = Math.max(headerCanvas.width, boardCanvas.width) + padding * 2;
      const height = padding + headerCanvas.height + gap + boardCanvas.height + padding;

      const composite = document.createElement('canvas');
      composite.width = width;
      composite.height = height;
      const ctx = composite.getContext('2d');
      ctx.fillStyle = '#0d1117';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(headerCanvas, (width - headerCanvas.width) / 2, padding);
      ctx.drawImage(boardCanvas, (width - boardCanvas.width) / 2, padding + headerCanvas.height + gap);

      return new Promise((resolve, reject) => {
        composite.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('toBlob returned null'))),
          'image/png'
        );
      });
    })();

    // text/html with the label + the image inlined as a base64 data URL.
    // Apps that support rich paste (Teams, Slack, Outlook, etc.) will
    // render both the text and the image from this single HTML payload.
    const htmlPromise = blobPromise.then(
      (imageBlob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const dataUrl = reader.result;
            const html = `<div style="font-weight:bold"><strong>${escapedLabel}</strong></div><img src="${dataUrl}" />`;
            resolve(new Blob([html], { type: 'text/html' }));
          };
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(imageBlob);
        })
    );

    // text/plain fallback for paste targets that can't render HTML
    // (e.g. a plain notes field).
    const textBlob = new Blob([label], { type: 'text/plain' });

    const downloadFallback = async () => {
      try {
        const blob = await blobPromise;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `dispatch-${tdrData?.inbound?.flightNumber || 'board'}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setShareStatus('downloaded');
        setTimeout(() => setShareStatus('idle'), 2400);
      } catch {
        setShareStatus('failed');
        setTimeout(() => setShareStatus('idle'), 2400);
      }
    };

    if (window.ClipboardItem && navigator.clipboard?.write) {
      navigator.clipboard
        .write([
          new window.ClipboardItem({
            'text/html': htmlPromise,
            'text/plain': textBlob,
            'image/png': blobPromise,
          }),
        ])
        .then(() => {
          setShareStatus('copied');
          setTimeout(() => setShareStatus('idle'), 2000);
        })
        .catch(downloadFallback);
    } else {
      downloadFallback();
    }
  }, [shareStatus, tdrData]);

  // Handle Magic Button - call backend OR-Tools solver
  const handleMagicButton = useCallback(async () => {
    // Unconfirmed ghosts (Brock-only flights) never go to the solver — the
    // plan hasn't accepted them. They stay in Unassigned untouched.
    const ghostHold = runs.unassigned.filter(f => f.isGhost && !f.ghostConfirmed);
    const solvable = runs.unassigned.filter(f => !(f.isGhost && !f.ghostConfirmed));
    if (solvable.length === 0) {
      alert('No unassigned flights to solve.');
      return;
    }
    setMagicLoading(true);
    try {
      const response = await fetch('/api/solve-routes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          flights: solvable,
          inboundETA: inboundETA,
          inboundGate: inboundGate,
          params: params,
          splitMode: splitMode,
          flightNote: flightNote,
        }),
      });
      // 429 = backend rate limiter. Pop the countdown dialog so the
      // dispatcher knows exactly how long to wait, instead of a generic
      // "Failed to solve routes" alert.
      if (response.status === 429) {
        const retryAfter = parseInt(response.headers.get('retry-after'), 10);
        setRateLimitSeconds(Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 60);
        setRateLimitOpen(true);
        setMagicLoading(false);
        return;
      }
      const result = await response.json();
      if (result.error) {
        setMagicLoading(false);
        alert(result.error);
        return;
      }
      // Preserve local drivers from current state
      const localDrivers = runs.drivers.filter(d => d.isLocalDriver);
      const solverDrivers = result.drivers || [];
      // Re-number solver driver IDs to avoid collision with local drivers
      let nextId = localDrivers.length > 0
        ? Math.max(...localDrivers.map(d => d.id)) + 1
        : 1;
      const renumberedDrivers = solverDrivers.map(d => {
        const driver = { ...d, id: nextId++ };
        // Apply "run in this order" to multi-stop non-local drivers
        if (driver.flights.length > 1) {
          driver.flights = driver.flights.map((f, i) => ({ ...f, runOrder: i + 1 }));
          driver.notes = driver.notes
            ? (driver.notes.includes('Run in this order please.')
              ? driver.notes
              : `${driver.notes} | Run in this order please.`)
            : 'Run in this order please.';
        }
        return driver;
      });
      setRuns({
        unassigned: [...(result.unassigned || []), ...ghostHold],
        drivers: [...renumberedDrivers, ...localDrivers],
      });

      // Surface any tiles the dispatcher needs to review (cancelled,
      // unparseable gate, or unassigned for any other reason). Re-map
      // driverIds to the renumbered driver IDs so the popup shows the
      // names the dispatcher actually sees.
      const flagged = result.flaggedForReview || [];
      if (flagged.length) {
        // Build a lookup from solver's original driver id → renumbered id
        const idMap = new Map();
        (result.drivers || []).forEach((d, idx) => {
          idMap.set(d.id, renumberedDrivers[idx]?.id);
        });
        const remapped = flagged.map((item) => {
          if (item.driverId == null) return item;
          const newId = idMap.get(item.driverId);
          const driver = renumberedDrivers.find((d) => d.id === newId);
          return {
            ...item,
            driverId: newId,
            driverName: driver?.name || item.driverName,
          };
        });
        setReviewItems(remapped);
        setReviewOpen(true);
      } else {
        setReviewItems([]);
        setReviewOpen(false);
      }
    } catch (err) {
      console.error('Solve routes error:', err);
      alert('Failed to solve routes. Check console for details.');
    } finally {
      setMagicLoading(false);
    }
  }, [runs, inboundETA, inboundGate, params, splitMode, flightNote]);

  // Handle adding freight item
  const handleAddFreightItem = () => {
    if (!freightInput.type || !freightInput.pieces || !freightInput.weight) {
      alert('Please fill in Type, Pieces, and Weight for freight');
      return;
    }

    // Create bin distribution object with only non-empty values
    const binDistribution = {};
    Object.entries(freightInput.bins).forEach(([bin, count]) => {
      if (count && parseInt(count) > 0) {
        binDistribution[bin] = parseInt(count);
      }
    });

    setCustomFlightData(prev => ({
      ...prev,
      freight: [...prev.freight, {
        type: freightInput.type,
        pieces: parseInt(freightInput.pieces),
        count: parseInt(freightInput.pieces), // Add count as well for compatibility
        weight: parseInt(freightInput.weight),
        bins: binDistribution
      }]
    }));

    // Reset freight input
    setFreightInput({
      type: '',
      pieces: '',
      weight: '',
      bins: { A: '', B: '', C: '', D: '', E: '', F: '' }
    });
  };

  // Handle deleting freight item from custom flight
  const handleDeleteCustomFreight = (index) => {
    setCustomFlightData(prev => ({
      ...prev,
      freight: prev.freight.filter((_, i) => i !== index)
    }));
  };

  // Handle creating custom flight
  const handleCreateCustomFlight = () => {
    // Validate required fields
    if (!customFlightData.flightNumber || !customFlightData.destination || !customFlightData.etd) {
      alert('Please fill in Flight Number, Destination, and ETD');
      return;
    }

    // Compile freightByBin from all freight items
    const freightByBin = {};
    customFlightData.freight.forEach(freightItem => {
      if (freightItem.bins) {
        Object.entries(freightItem.bins).forEach(([bin, count]) => {
          if (count > 0) {
            freightByBin[bin] = (freightByBin[bin] || 0) + count;
          }
        });
      }
    });

    // Compile binDistribution from bag bins
    const binDistribution = {};
    if (customFlightData.bagBins) {
      Object.entries(customFlightData.bagBins).forEach(([bin, count]) => {
        const numCount = parseInt(count) || 0;
        if (numCount > 0) {
          binDistribution[bin] = numCount;
        }
      });
    }

    // Create temporary flight object to check if it's overnight
    const tempFlight = {
      etd: customFlightData.etd,
      destination: customFlightData.destination
    };

    // Calculate connection time and category
    const isOvernight = isOvernightFlight(tempFlight);
    const connectionTime = inboundETA ? calculateConnectionTime(customFlightData.etd, inboundETA, isOvernight) : null;
    const calculatedCategory = connectionTime !== null ? getConnectionCategory(connectionTime, params, isOvernight) : customFlightData.connectionCategory;

    // Create new flight object
    const newFlight = {
      uniqueId: `custom-${Date.now()}`,
      flightNumber: customFlightData.flightNumber,
      originalFlightNumber: customFlightData.flightNumber,
      destination: customFlightData.destination,
      etd: customFlightData.etd,
      gate: customFlightData.gate || 'TBD',
      bags: parseInt(customFlightData.bags) || 0,
      binDistribution: binDistribution, // Include bag bin distribution
      connectionCategory: calculatedCategory, // Use calculated category if connection time available
      connectionTime: connectionTime, // Calculate connection time from ETD
      isOvernight: isOvernight, // Mark if overnight
      notes: customFlightData.notes || '',
      isCustom: true, // Mark as custom so we know it was manually created
      isMultiDestination: false,
      destinations: [],
      allFreight: customFlightData.freight || [], // Include freight items
      freightByBin: freightByBin, // Include freight bin distribution
      isFreightOnly: customFlightData.connectionCategory === 'freight-only' || (customFlightData.freight.length > 0 && customFlightData.bags === 0)
    };

    // Add to unassigned flights
    setRuns(prevRuns => ({
      ...prevRuns,
      unassigned: [...prevRuns.unassigned, newFlight]
    }));

    // Reset form and close modal
    setCustomFlightData({
      flightNumber: '',
      destination: '',
      etd: '',
      gate: '',
      bags: 0,
      bagBins: { A: '', B: '', C: '', D: '', E: '', F: '' },
      connectionCategory: 'normal',
      notes: '',
      freight: []
    });
    setFreightInput({
      type: '',
      pieces: '',
      weight: '',
      bins: { A: '', B: '', C: '', D: '', E: '', F: '' }
    });
    setCustomFlightModalOpen(false);
  };

  // Handle closing custom flight modal
  const handleCloseCustomFlightModal = () => {
    // Reset form
    setCustomFlightData({
      flightNumber: '',
      destination: '',
      etd: '',
      gate: '',
      bags: 0,
      bagBins: { A: '', B: '', C: '', D: '', E: '', F: '' },
      connectionCategory: 'normal',
      notes: '',
      freight: []
    });
    setFreightInput({
      type: '',
      pieces: '',
      weight: '',
      bins: { A: '', B: '', C: '', D: '', E: '', F: '' }
    });
    setCustomFlightModalOpen(false);
  };

  return (
    // The board used to live inside a collapsible Accordion; the dispatch
    // board is the page's whole point, so it's a plain Paper now — always
    // visible, no drawer behavior. (Paper keeps the MUI class the themes
    // already style.)
    <Paper
      className="dispatch-board-shell"
      sx={{
        backgroundColor: '#1f2937',
        color: '#e2e2e2',
        mb: 4,
      }}
    >
      <Box sx={{ py: 1.5, px: 2 }}>
        <Box className="board-inbound-strip" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
          {tdrData?.inbound ? (
            <Box ref={headerCaptureRef} sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Typography sx={{
                color: '#C0C0C0',
                fontSize: '1.1rem',
                fontWeight: 700
              }}>
                WN {tdrData.inbound.flightNumber}
              </Typography>
              <Divider orientation="vertical" flexItem sx={{ bgcolor: '#C0C0C0', height: '24px' }} />
              <Typography sx={{ 
                color: '#C0C0C0', 
                fontSize: '1.1rem',
                fontWeight: 700
              }}>
                {tdrData.inbound.arrivingFrom || 'Unknown'}
              </Typography>
              <Divider orientation="vertical" flexItem sx={{ bgcolor: '#C0C0C0', height: '24px' }} />
              <Typography sx={{ 
                color: '#C0C0C0',
                fontSize: '1.1rem',
                fontWeight: 700
              }}>
                {tdrData.inbound.arrivalGate || 'TBD'}
              </Typography>
              <Divider orientation="vertical" flexItem sx={{ bgcolor: '#C0C0C0', height: '24px' }} />
              <Typography sx={{ 
                color: '#C0C0C0',
                fontSize: '1.1rem',
                fontWeight: 700
              }}>
                ETA {tdrData.inbound.eta || '--:--'}
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ 
              color: '#C0C0C0',
              fontSize: '1.1rem',
              fontWeight: 700
            }}>
              Flight Dispatch Board
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ px: 2, pb: 2 }}>
        <Box sx={{ p: 2, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, display: 'flex', gap: 2 }}>
          {/* Left Sidebar + Custom Tile: one stretch row, so the triple-
              stacked button always spans exactly the two button rows —
              no hardcoded height to drift when a theme's font metrics
              change the row heights. */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', alignSelf: 'flex-start' }}>
          {/* Left Sidebar - Two Rows */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: '140px' }}>
            {/* Top Row */}
            <Box sx={{ display: 'flex', gap: 0.75 }}>
              <Button
                  variant="outlined"
                  onClick={handleAddDriver}
                  size="small"
                  sx={{ height: '26px', lineHeight: 1, fontSize: '0.7rem', flex: 1, borderColor: '#4CAF50', color: '#4CAF50', '&:hover': { borderColor: '#45a049', backgroundColor: 'rgba(76, 175, 80, 0.1)' } }}
              >
                  +Driver
              </Button>

              <Button
                  variant="outlined"
                  onClick={() => setHideColdGates(!hideColdGates)}
                  size="small"
                  sx={{ height: '26px', lineHeight: 1, fontSize: '0.7rem', flex: 1, borderColor: '#87CEEB', color: '#87CEEB', whiteSpace: 'nowrap', '&:hover': { borderColor: '#5F9EA0', backgroundColor: 'rgba(135, 206, 235, 0.1)' } }}
              >
                  {hideColdGates ? 'Show Cold' : 'Hide Cold'}
              </Button>
            </Box>

            {/* Bottom Row */}
            <Box sx={{ display: 'flex', gap: 0.75 }}>
              <Button
                onClick={() => setSplitMode(!splitMode)}
                variant={splitMode ? "contained" : "outlined"}
                size="small"
                sx={{
                  height: '26px', lineHeight: 1, fontSize: '0.7rem', flex: 1,
                  borderColor: '#ffffff',
                  backgroundColor: splitMode ? '#ffffff' : 'transparent',
                  color: splitMode ? '#000' : '#ffffff',
                  '&:hover': {
                    borderColor: '#e0e0e0',
                    backgroundColor: splitMode ? '#e0e0e0' : 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                FWD/AFT
              </Button>

              <Button
                onClick={toggleAddMode}
                variant={addMode ? "contained" : "outlined"}
                size="small"
                sx={{
                  height: '26px', lineHeight: 1, fontSize: '0.7rem', flex: 1, whiteSpace: 'nowrap',
                  borderColor: '#FF9800',
                  backgroundColor: addMode ? '#FF9800' : 'transparent',
                  color: addMode ? '#000' : '#FF9800',
                  '&:hover': {
                    borderColor: '#F57C00',
                    backgroundColor: addMode ? '#F57C00' : 'rgba(255, 152, 0, 0.1)'
                  }
                }}
              >
                {addMode ? 'Exit Add' : 'Add Mode'}
              </Button>
            </Box>
          </Box>

          {/* Custom Flight Tile Button - Triple Stacked - Right of Left Sidebar */}
          <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
            <Button
              variant="outlined"
              onClick={() => setCustomFlightModalOpen(true)}
              size="small"
              sx={{
                height: '100%',
                minWidth: '90px',
                fontSize: '0.65rem',
                fontWeight: 'bold',
                borderColor: '#F9B612',
                color: '#F9B612',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '4px 8px',
                textTransform: 'uppercase',
                lineHeight: 1.1,
                whiteSpace: 'nowrap',
                '&:hover': {
                  borderColor: '#e6a700',
                  backgroundColor: 'rgba(249, 182, 18, 0.1)'
                },
              }}
            >
              <Box sx={{ fontSize: '0.65rem', fontWeight: 'bold' }}>CUSTOM</Box>
              <Box sx={{ fontSize: '0.65rem', fontWeight: 'bold' }}>FLIGHT</Box>
              <Box sx={{ fontSize: '0.65rem', fontWeight: 'bold' }}>TILE</Box>
            </Button>
          </Box>
          </Box>

          {/* Right Side - Main Button Area */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', paddingRight: '320px' }}>
            {/* Top Right Buttons - Share + All Driver Notes + Print */}
            <Box sx={{ position: 'absolute', top: 0, right: 0, display: 'flex', gap: 1 }}>
              {/* Share to Teams Button */}
              <Button
                variant="contained"
                onClick={() => {
                  if (getUnresolvedGhosts().length > 0) {
                    setGhostGate({ action: 'share' });
                    return;
                  }
                  handleShareToTeams();
                }}
                disabled={!tdrData?.inbound || shareStatus === 'busy'}
                size="small"
                sx={{
                  minHeight: '58.5px',
                  minWidth: '110px',
                  whiteSpace: 'nowrap',
                  backgroundColor:
                    shareStatus === 'copied' ? '#2e7d32' :
                    shareStatus === 'downloaded' ? '#2e7d32' :
                    shareStatus === 'failed' ? '#c62828' :
                    '#1976d2',
                  color: 'white',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '8px 12px',
                  '&:hover': {
                    backgroundColor:
                      shareStatus === 'copied' || shareStatus === 'downloaded' ? '#1b5e20' :
                      shareStatus === 'failed' ? '#b71c1c' :
                      '#1565c0',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'rgba(255, 255, 255, 0.12)',
                    color: 'rgba(255, 255, 255, 0.4)',
                  },
                }}
              >
                <ContentCopyIcon sx={{ fontSize: '20px', marginRight: '8px' }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.1 }}>
                  {shareStatus === 'busy' ? (
                    <>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.1, fontWeight: 500 }}>COPYING</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.1, fontWeight: 500 }}>…</Typography>
                    </>
                  ) : shareStatus === 'copied' ? (
                    <>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.1, fontWeight: 500 }}>COPIED!</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.7rem', lineHeight: 1.1, fontWeight: 500 }}>{PASTE_SHORTCUT} in Teams</Typography>
                    </>
                  ) : shareStatus === 'downloaded' ? (
                    <>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.1, fontWeight: 500 }}>SAVED</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.7rem', lineHeight: 1.1, fontWeight: 500 }}>PNG</Typography>
                    </>
                  ) : shareStatus === 'failed' ? (
                    <>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.1, fontWeight: 500 }}>FAILED</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.7rem', lineHeight: 1.1, fontWeight: 500 }}>retry</Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.1, fontWeight: 500 }}>SHARE</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.1, fontWeight: 500 }}>TO TEAMS</Typography>
                    </>
                  )}
                </Box>
              </Button>

              {/* All Driver Notes Button */}
              <Button
                variant="contained"
                onClick={() => {
                  setEditingFlightNote(flightNote || '');
                  setFlightNoteModalOpen(true);
                }}
                size="small"
                sx={{
                  minHeight: '58.5px',
                  minWidth: '80px',
                  backgroundColor: flightNote ? '#F9B612' : '#6c757d',
                  color: flightNote ? '#000' : 'white',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '8px 12px',
                  '&:hover': {
                    backgroundColor: flightNote ? '#e6a700' : '#5a6268',
                  },
                }}
              >
                <NotesIcon sx={{ fontSize: '20px', marginRight: '8px' }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.1 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.1, fontWeight: 500 }}>ALL DRV</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.1, fontWeight: 500 }}>NOTES</Typography>
                </Box>
              </Button>

              {/* Print Button */}
              <Button
                variant="contained"
                onClick={() => {
                  if (getUnresolvedGhosts().length > 0) {
                    setGhostGate({ action: 'print' });
                    return;
                  }
                  triggerPrint();
                }}
                disabled={runs.drivers.every(d => d.flights.length === 0)}
                size="small"
                sx={{
                  minHeight: '58.5px',
                  minWidth: '80px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '8px 12px',
                  '&:hover': {
                    backgroundColor: '#5a6268',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'rgba(255, 255, 255, 0.12)',
                  },
                }}
              >
                <PrintIcon sx={{ fontSize: '20px', marginRight: '8px' }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.1 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.1, fontWeight: 500 }}>PRINT</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.1, fontWeight: 500 }}>RUNS</Typography>
                </Box>
              </Button>
              
              {/* Hidden PrintDispatchButton for actual print functionality */}
              <Box sx={{ display: 'none' }} data-print-dispatch="true">
                <PrintDispatchButton 
                  runs={runs} 
                  tdrData={tdrData} 
                  brockData={brockData} 
                  disabled={runs.drivers.every(d => d.flights.length === 0)}
                  hideColdGates={hideColdGates}
                />
              </Box>
            </Box>

            {/* First Row - Category Assigns + Magic */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', justifyContent: 'center', mb: 1 }}>
              <Button onClick={() => handleAutoAssign('HOT')} variant="contained" size="small" sx={{ height: '26px', lineHeight: 1, minWidth: '70px', fontSize: '0.7rem', backgroundColor: '#d32f2f', '&:hover': { backgroundColor: '#c62828'} }}>Hot</Button>
              <Button onClick={() => handleAutoAssign('PRIORITY')} variant="contained" size="small" sx={{ height: '26px', lineHeight: 1, minWidth: '70px', fontSize: '0.7rem', backgroundColor: '#F9B612', color: '#000', fontWeight: 'bold', '&:hover': { backgroundColor: '#e6a700'} }}>Priority</Button>
              <Button onClick={() => handleAutoAssign('COLD')} variant="contained" size="small" sx={{ height: '26px', lineHeight: 1, minWidth: '70px', fontSize: '0.7rem', backgroundColor: '#0277BD', '&:hover': { backgroundColor: '#01579B' } }}>Cold</Button>
              <Button onClick={() => handleAutoAssign('ASSIGN_FREIGHT_ONLY')} variant="contained" size="small" sx={{ height: '26px', lineHeight: 1, minWidth: '70px', fontSize: '0.7rem', backgroundColor: '#F6530F', '&:hover': { backgroundColor: '#E5480E' } }}>Freight Run</Button>

              <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', height: '20px', mx: 0.5 }} />

              {/* Autoroute (the artist formerly known as Magic Button) —
                  .comp-acty while solving so themes can animate it */}
              <Button onClick={handleMagicButton} className={magicLoading ? 'comp-acty' : undefined} variant="contained" size="small" startIcon={<RouteIcon sx={{ fontSize: '0.9rem' }} />} sx={{ height: '26px', lineHeight: 1, minWidth: '100px', fontSize: '0.7rem', fontWeight: 'bold', backgroundColor: '#7B1FA2', '&:hover': { backgroundColor: '#6A1B9A' } }} disabled={runs.unassigned.length === 0}>Autoroute</Button>
            </Box>

            {/* Second Row - Location Groups + Reset */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', justifyContent: 'center' }}>
              <Button onClick={() => handleAutoAssign('SOLO')} variant="contained" size="small" sx={{ height: '26px', lineHeight: 1, minWidth: '70px', fontSize: '0.7rem', backgroundColor: '#ffffff', color: '#000', '&:hover': { backgroundColor: '#e0e0e0' } }}>Solo</Button>
              <Button onClick={() => handleAutoAssign('ODDS_EVENS')} variant="contained" size="small" sx={{ height: '26px', lineHeight: 1, minWidth: '70px', fontSize: '0.7rem', backgroundColor: '#ffffff', color: '#000', '&:hover': { backgroundColor: '#e0e0e0' } }}>Odds/Evens</Button>
              <Button onClick={() => handleAutoAssign('EAST_WEST')} variant="contained" size="small" sx={{ height: '26px', lineHeight: 1, minWidth: '70px', fontSize: '0.7rem', backgroundColor: '#ffffff', color: '#000', '&:hover': { backgroundColor: '#e0e0e0' } }}>East/West</Button>
              <Button onClick={() => handleAutoAssign('SECTORS')} variant="contained" size="small" sx={{ height: '26px', lineHeight: 1, minWidth: '70px', fontSize: '0.7rem', backgroundColor: '#ffffff', color: '#000', '&:hover': { backgroundColor: '#e0e0e0' } }}>Sectors</Button>
              <Button onClick={() => handleAutoAssign('QUADRANTS')} variant="contained" size="small" sx={{ height: '26px', lineHeight: 1, minWidth: '70px', fontSize: '0.7rem', backgroundColor: '#ffffff', color: '#000', '&:hover': { backgroundColor: '#e0e0e0' } }}>Quadrants</Button>

              <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', height: '20px', mx: 0.5 }} />

              <Button onClick={() => handleAutoAssign('UNASSIGN_ALL')} variant="contained" size="small" sx={{ height: '26px', lineHeight: 1, minWidth: '70px', fontSize: '0.7rem', backgroundColor: '#d32f2f', '&:hover': { backgroundColor: '#c62828' } }} disabled={runs.drivers.length === 0}>Reset All</Button>
            </Box>
          </Box>
        </Box>

        {/* Active Mode Indicators */}
        {(addMode || splitMode) && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            {addMode && selectedDriverId && (
              <Typography variant="body2" sx={{ color: '#FF9800', fontWeight: 'bold', mb: 0.5 }}>
                ➤ Adding to: {runs.drivers.find(d => d.id === selectedDriverId)?.name}
              </Typography>
            )}
            {splitMode && (
              <Typography variant="body2" sx={{ color: '#9C27B0', fontWeight: 'bold' }}>
                ⚡ FWD/AFT Split Mode Active
              </Typography>
            )}
          </Box>
        )}

        <DndContext
          onDragStart={handleDragStart}
          onDragMove={handleDragMove}
          onDragEnd={handleDragEnd}
        >
          {/* Horizontal Scroll Container */}
          <Box sx={{
            overflowX: 'auto',
            overflowY: 'hidden',
            width: '100%',
            paddingBottom: '12px', // Space for horizontal scrollbar
            '&::-webkit-scrollbar': {
              height: '12px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(255,255,255,0.1)',
              borderRadius: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(255,255,255,0.3)',
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.5)',
              },
            },
          }}>
            {/* Grid Layout with Fixed Column Widths */}
            <Box ref={boardGridRef} sx={{
              display: 'grid',
              gridTemplateColumns: runs.drivers.length === 0
                ? '320px'
                : `320px repeat(${runs.drivers.length}, 320px)`,
              gap: 2,
              minWidth: 'min-content',
              width: 'max-content',
            }}>

              {/* Fixed-width Unassigned Flights Column */}
              <Box data-share-exclude="true" className="board-column-shell" sx={{
                backgroundColor: 'rgba(0,0,0,0.3)',
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
                border: '2px solid rgba(255,255,255,0.1)'
              }}>
                {/* Mirrors the driver headers' two-row structure 1:1 —
                    hidden twins of their icon buttons reserve identical row
                    heights, so every column header ends on the same line in
                    every theme without any hardcoded height. */}
                <Box
                  className="board-column-head"
                  sx={{
                    p: 1.5,
                    backgroundColor: 'rgba(0,0,0,0.2)',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 'bold', fontSize: '0.95rem', lineHeight: 1.2, mb: 0.25 }}
                      >
                        Unassigned Tiles ({runs.unassigned.length})
                      </Typography>
                      <IconButton size="small" disabled aria-hidden sx={{ visibility: 'hidden' }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      {(() => {
                        // Workload still on the table: bags (with FWD/AFT
                        // split when bin data exists), freight totals on
                        // their own line. Both lines tuck inside the height
                        // the hidden icon button already reserves, so this
                        // header stays level with the drivers'.
                        // Split tiles carry allFreight on exactly one half,
                        // so summing across tiles never double-counts.
                        const flights = runs.unassigned;
                        const bags = flights.reduce((t, f) => t + (f.bags || 0), 0);
                        let fwd = 0, aft = 0;
                        flights.forEach((f) => {
                          const s = getFwdAftBags(f);
                          fwd += s.fwdBags;
                          aft += s.aftBags;
                        });
                        const items = flights.flatMap((f) => f.allFreight || []);
                        const pieces = items.reduce((t, i) => t + (parseInt(i.pieces, 10) || 0), 0);
                        const weight = items.reduce((t, i) => t + (parseInt(i.weight, 10) || 0), 0);
                        const captionSx = {
                          fontSize: '0.75rem',
                          color: 'rgba(255,255,255,0.8)',
                          lineHeight: 1,
                          whiteSpace: 'nowrap'
                        };
                        return (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
                            <Typography variant="caption" sx={captionSx}>
                              {`${bags} ${bags === 1 ? 'Bag' : 'Bags'}`
                                + (fwd > 0 || aft > 0 ? ` · FWD ${fwd} / AFT ${aft}` : '')}
                            </Typography>
                            {pieces > 0 && (
                              <Typography variant="caption" sx={captionSx}>
                                {`Frt ${pieces} pc / ${weight.toLocaleString()} lbs`}
                              </Typography>
                            )}
                          </Box>
                        );
                      })()}
                      <IconButton size="small" disabled aria-hidden sx={{ visibility: 'hidden' }}>
                        <NotesIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{
                  flexGrow: 1,
                  p: 1
                }}>
                  <DroppableColumn
                    id="unassigned"
                    title=""
                    flights={runs.unassigned}
                    params={params}
                    onFlightEdit={handleFlightEdit}
                    onGhostConfirm={handleGhostConfirm}
                    onGhostDismiss={handleGhostDismiss}
                    hideColdGates={hideColdGates}
                  />
                </Box>
              </Box>

              {/* Fixed-width Driver Columns */}
              {runs.drivers.map(driver => (
                <Box
                  key={driver.id}
                  className="board-column-shell"
                  sx={{
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    borderRadius: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    border: '2px solid rgba(255,255,255,0.1)'
                  }}
                >
                  <Box
                    className={addMode && selectedDriverId === driver.id
                      ? 'board-column-head board-column-head--selected'
                      : 'board-column-head'}
                    sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1.5,
                    backgroundColor: addMode && selectedDriverId === driver.id ? 'rgba(255, 152, 0, 0.2)' : 'rgba(0,0,0,0.2)',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    cursor: addMode ? 'pointer' : 'default'
                  }}
                  onClick={() => handleDriverSelect(driver.id)}>
                    <Box sx={{ flex: 1 }}>
                      {editingDriverId === driver.id ? (
                        <TextField
                          value={editingDriverName}
                          onChange={(e) => setEditingDriverName(e.target.value)}
                          variant="outlined"
                          size="small"
                          sx={{
                            mb: 0.5,
                            '& .MuiOutlinedInput-root': {
                              color: '#fff',
                              '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                              '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                              '&.Mui-focused fieldset': { borderColor: '#1976d2' }
                            }
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={handleSaveDriverName} size="small" sx={{ color: '#4CAF50' }}>
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                                <IconButton onClick={handleCancelEdit} size="small" sx={{ color: '#f44336' }}>
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                          onKeyPress={(e) => e.key === 'Enter' && handleSaveDriverName()}
                        />
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 'bold',
                              fontSize: '0.95rem',
                              lineHeight: 1.2,
                              mb: 0.25
                            }}
                          >
                            {driver.name}
                          </Typography>
                          <IconButton
                            onClick={() => handleEditDriverName(driver.id, driver.name)}
                            size="small"
                            sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#1976d2' } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          {!driver.isLocalDriver && driver.flights.length > 0 && process.env.NODE_ENV === 'development' && (
                            <IconButton
                              onClick={() => {
                                // Stash the run snapshot so the demo page can pick it up
                                // in a fresh tab. Opening in a new tab preserves the
                                // dispatch board state in this tab.
                                const payload = {
                                  driver,
                                  inboundETA,
                                  inboundGate,
                                  inboundFlightNumber: tdrData?.inbound?.flightNumber,
                                  inboundFrom: tdrData?.inbound?.arrivingFrom,
                                  otherDriverCount: runs.drivers.filter(d =>
                                    d.id !== driver.id &&
                                    !d.isLocalDriver &&
                                    d.flights.length > 0
                                  ).length,
                                  savedAt: Date.now(),
                                };
                                try {
                                  localStorage.setItem('driverDemoPayload', JSON.stringify(payload));
                                } catch (e) {
                                  console.warn('Could not save driverDemoPayload', e);
                                }
                                window.open('/driver-demo', '_blank');
                              }}
                              size="small"
                              title="Open driver scanner demo (new tab)"
                              sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#22d3ee' } }}
                            >
                              <PhoneAndroidIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      )}
                      
                      {editingDriverNotes === driver.id ? (
                        <TextField
                          value={editingNotes}
                          onChange={(e) => setEditingNotes(e.target.value)}
                          placeholder="Add notes..."
                          variant="outlined"
                          size="small"
                          multiline
                          rows={2}
                          sx={{
                            width: '100%',
                            '& .MuiOutlinedInput-root': {
                              color: '#fff',
                              fontSize: '0.75rem',
                              '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                              '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                              '&.Mui-focused fieldset': { borderColor: '#1976d2' }
                            }
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={handleSaveDriverNotes} size="small" sx={{ color: '#4CAF50' }}>
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                                <IconButton onClick={handleCancelEdit} size="small" sx={{ color: '#f44336' }}>
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                        />
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography
                            variant="caption"
                            onClick={() => driver.flights.length > 0 && setRouteModalDriver(driver)}
                            sx={{
                              fontSize: '0.75rem',
                              color: 'rgba(255,255,255,0.8)',
                              lineHeight: 1,
                              cursor: driver.flights.length > 0 ? 'pointer' : 'default',
                              '&:hover': driver.flights.length > 0 ? {
                                color: '#4CAF50',
                                textDecoration: 'underline'
                              } : {}
                            }}
                          >
                            {(() => {
                              const stops = physicalStopCount(driver.flights);
                              const bags = driver.flights.reduce((total, flight) => total + (flight.bags || 0), 0);
                              return `${stops} ${stops === 1 ? 'Stop' : 'Stops'} / ${bags} ${bags === 1 ? 'Bag' : 'Bags'}`;
                            })()}
                          </Typography>
                          {driver.notes && (
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.7rem',
                                color: 'rgba(255,255,255,0.6)',
                                fontStyle: 'italic',
                                ml: 1
                              }}
                            >
                              {driver.notes}
                            </Typography>
                          )}
                          <IconButton
                            onClick={() => handleEditDriverNotes(driver.id, driver.notes)}
                            size="small"
                            sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#1976d2' } }}
                          >
                            <NotesIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                    {(!driver.isLocalDriver || driver.name.includes('FWD') || driver.name.includes('AFT')) && (
                      <IconButton
                        onClick={() => handleRemoveDriver(driver.id)}
                        size="small"
                        sx={{
                          color: 'rgba(255,255,255,0.4)',
                          '&:hover': {
                            color: '#FF0000',
                            backgroundColor: 'rgba(255,0,0,0.1)'
                          }
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  <Box sx={{
                    flexGrow: 1,
                    p: 1
                  }}>
                    <DroppableColumn
                      id={`driver-${driver.id}`}
                      title=""
                      flights={driver.flights}
                      params={params}
                      onRemoveFlight={handleRemoveFlight}
                      onFlightEdit={handleFlightEdit}
                    onGhostConfirm={handleGhostConfirm}
                    onGhostDismiss={handleGhostDismiss}
                      hideColdGates={hideColdGates}
                      onPinClick={handlePinClick}
                      onSplitLocal={driver.isLocalDriver ? () => handleSplitLocal(driver.id) : undefined}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Use createPortal to render overlay at document body level */}
          {activeFlight && createPortal(
            <DragOverlay>
              <FlightTile
                flight={activeFlight}
                sourceColumnId=""
                params={params}
                isDragOverlay={true}
                hideColdGates={hideColdGates}
              />
            </DragOverlay>,
            document.body
          )}
        </DndContext>

        {/* Outbound gate: undecided ghosts when printing or sharing. */}
        <Dialog
          open={Boolean(ghostGate)}
          onClose={() => setGhostGate(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { backgroundColor: '#1a2332', color: '#fff', border: '1px solid rgba(249,182,18,0.4)' } }}
        >
          <DialogTitle sx={{ color: '#F9B612', fontWeight: 700, pb: 1 }}>
            Unresolved Ghost Tiles
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem', mb: 1.5 }}>
              These scanned flights are still waiting on a Confirm or Dismiss:
            </Typography>
            <Box sx={{
              backgroundColor: 'rgba(0,0,0,0.25)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderLeft: '3px solid #F9B612',
              borderRadius: 1.5,
              overflow: 'hidden',
            }}>
              {getUnresolvedGhosts().map((f, idx, arr) => (
                <Typography key={f.uniqueId} sx={{
                  color: '#fff', fontSize: '0.88rem', px: 1.5, py: 1, lineHeight: 1.45,
                  borderBottom: idx === arr.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.05)',
                }}>
                  {f.flightNumber} to {f.destination} — {f.bags} bag{f.bags === 1 ? '' : 's'}
                </Typography>
              ))}
            </Box>
            <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.8rem', mt: 1.5 }}>
              Unassigned ghosts will not be on any run sheet. Confirm or dismiss
              them, or continue without them.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, pt: 1, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <Button
              onClick={() => {
                const action = ghostGate?.action;
                setGhostGate(null);
                if (action === 'print') triggerPrint();
                if (action === 'share') handleShareToTeams();
              }}
              sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'none', fontWeight: 700 }}
            >
              {ghostGate?.action === 'share' ? 'Share Anyway' : 'Print Anyway'}
            </Button>
            <Button
              onClick={() => setGhostGate(null)}
              variant="contained"
              sx={{
                backgroundColor: '#F9B612', color: '#000',
                fontWeight: 700, textTransform: 'none', px: 3,
                '&:hover': { backgroundColor: '#e0a410' },
              }}
            >
              Go Back
            </Button>
          </DialogActions>
        </Dialog>

        {/* Rate-limit countdown — fired when the backend returns 429
            on /api/solve-routes (Magic Button). */}
        <RateLimitDialog
          open={rateLimitOpen}
          retryAfterSeconds={rateLimitSeconds}
          onClose={() => setRateLimitOpen(false)}
        />

        {/* Solver loading — theme-aware ritual + per-theme name
            (the rabbit lives on in the default theme) */}
        <SolverLoadingModal open={magicLoading} />

        {/* Custom Flight Modal */}
        <Dialog
          open={customFlightModalOpen}
          onClose={handleCloseCustomFlightModal}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: 'rgba(31, 41, 55, 0.95)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              color: '#fff'
            }
          }}
        >
          <DialogTitle sx={{ color: '#fff', backgroundColor: 'transparent' }}>
            Create Custom Flight
          </DialogTitle>
          <DialogContent sx={{ backgroundColor: 'transparent' }}>
            <Box
              component="form"
              id="custom-flight-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleCreateCustomFlight();
              }}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
            >
              {/* Flight Number */}
              <TextField
                label="Flight Number"
                value={customFlightData.flightNumber}
                onChange={(e) => setCustomFlightData(prev => ({ ...prev, flightNumber: e.target.value }))}
                placeholder="e.g., WN 123"
                size="small"
                required
                sx={{
                  '& .MuiInputLabel-root': { color: '#ccc' },
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: '#555' },
                    '&:hover fieldset': { borderColor: '#777' },
                    '&.Mui-focused fieldset': { borderColor: '#2196f3' }
                  }
                }}
              />

              {/* Destination */}
              <TextField
                label="Destination"
                value={customFlightData.destination}
                onChange={(e) => setCustomFlightData(prev => ({ ...prev, destination: e.target.value }))}
                placeholder="e.g., PHX"
                size="small"
                required
                sx={{
                  '& .MuiInputLabel-root': { color: '#ccc' },
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: '#555' },
                    '&:hover fieldset': { borderColor: '#777' },
                    '&.Mui-focused fieldset': { borderColor: '#2196f3' }
                  }
                }}
              />

              {/* ETD */}
              <TextField
                label="ETD"
                value={customFlightData.etd}
                onChange={(e) => setCustomFlightData(prev => ({ ...prev, etd: e.target.value }))}
                placeholder="e.g., 10:30"
                size="small"
                required
                sx={{
                  '& .MuiInputLabel-root': { color: '#ccc' },
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: '#555' },
                    '&:hover fieldset': { borderColor: '#777' },
                    '&.Mui-focused fieldset': { borderColor: '#2196f3' }
                  }
                }}
              />

              {/* Gate */}
              <TextField
                label="Gate"
                value={customFlightData.gate}
                onChange={(e) => setCustomFlightData(prev => ({ ...prev, gate: e.target.value }))}
                placeholder="e.g., C42"
                size="small"
                sx={{
                  '& .MuiInputLabel-root': { color: '#ccc' },
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: '#555' },
                    '&:hover fieldset': { borderColor: '#777' },
                    '&.Mui-focused fieldset': { borderColor: '#2196f3' }
                  }
                }}
              />

              {/* Bags */}
              <TextField
                label="Bags"
                type="number"
                value={customFlightData.bags}
                onChange={(e) => setCustomFlightData(prev => ({ ...prev, bags: e.target.value }))}
                placeholder="0"
                size="small"
                sx={{
                  '& .MuiInputLabel-root': { color: '#ccc' },
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: '#555' },
                    '&:hover fieldset': { borderColor: '#777' },
                    '&.Mui-focused fieldset': { borderColor: '#2196f3' }
                  }
                }}
              />

              {/* Bag Bin Distribution */}
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ color: '#999', mb: 0.5, display: 'block' }}>
                  Bag Bin Distribution (optional - for FWD/AFT display)
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 0.5 }}>
                  {['A', 'B', 'C', 'D', 'E', 'F'].map(bin => (
                    <TextField
                      key={bin}
                      label={bin}
                      type="number"
                      value={customFlightData.bagBins[bin]}
                      onChange={(e) => setCustomFlightData(prev => ({
                        ...prev,
                        bagBins: { ...prev.bagBins, [bin]: e.target.value }
                      }))}
                      size="small"
                      sx={{
                        '& .MuiInputLabel-root': { color: '#ccc', fontSize: '0.75rem' },
                        '& .MuiOutlinedInput-root': {
                          color: '#fff',
                          fontSize: '0.875rem',
                          '& fieldset': { borderColor: '#555' },
                          '&:hover fieldset': { borderColor: '#777' },
                          '&.Mui-focused fieldset': { borderColor: '#2196f3' }
                        },
                        '& .MuiInputBase-input': {
                          padding: '6px 8px'
                        }
                      }}
                    />
                  ))}
                </Box>
                <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
                  A/B/C = FWD | D/E/F = AFT
                </Typography>
              </Box>

              {/* Connection Category */}
              <FormControl size="small" sx={{
                '& .MuiInputLabel-root': { color: '#ccc' },
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': { borderColor: '#555' },
                  '&:hover fieldset': { borderColor: '#777' },
                  '&.Mui-focused fieldset': { borderColor: '#2196f3' }
                },
                '& .MuiSvgIcon-root': { color: '#ccc' }
              }}>
                <InputLabel>Connection Category</InputLabel>
                <Select
                  value={customFlightData.connectionCategory}
                  onChange={(e) => setCustomFlightData(prev => ({ ...prev, connectionCategory: e.target.value }))}
                  label="Connection Category"
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: 'rgba(31, 41, 55, 0.98)',
                        color: '#fff',
                        '& .MuiMenuItem-root': {
                          color: '#fff',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(33, 150, 243, 0.3)',
                            '&:hover': {
                              backgroundColor: 'rgba(33, 150, 243, 0.4)'
                            }
                          }
                        }
                      }
                    }
                  }}
                >
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="hot">Hot</MenuItem>
                  <MenuItem value="priority">Priority</MenuItem>
                  <MenuItem value="cold">Cold</MenuItem>
                  <MenuItem value="freight-only">Freight-Only</MenuItem>
                  <MenuItem value="international">International</MenuItem>
                  <MenuItem value="overnight">Overnight</MenuItem>
                  <MenuItem value="thru">Thru</MenuItem>
                </Select>
              </FormControl>

              {/* Freight Input Section */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ color: '#ccc', mb: 1 }}>
                  Add Freight Items
                </Typography>

                {/* Freight Input Fields */}
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    label="Type"
                    value={freightInput.type}
                    onChange={(e) => setFreightInput(prev => ({ ...prev, type: e.target.value }))}
                    placeholder="e.g., NFG, HUM"
                    size="small"
                    sx={{
                      flex: 1,
                      '& .MuiInputLabel-root': { color: '#ccc' },
                      '& .MuiOutlinedInput-root': {
                        color: '#fff',
                        '& fieldset': { borderColor: '#555' },
                        '&:hover fieldset': { borderColor: '#777' },
                        '&.Mui-focused fieldset': { borderColor: '#2196f3' }
                      }
                    }}
                  />
                  <TextField
                    label="Pieces"
                    type="number"
                    value={freightInput.pieces}
                    onChange={(e) => setFreightInput(prev => ({ ...prev, pieces: e.target.value }))}
                    placeholder="0"
                    size="small"
                    sx={{
                      width: '100px',
                      '& .MuiInputLabel-root': { color: '#ccc' },
                      '& .MuiOutlinedInput-root': {
                        color: '#fff',
                        '& fieldset': { borderColor: '#555' },
                        '&:hover fieldset': { borderColor: '#777' },
                        '&.Mui-focused fieldset': { borderColor: '#2196f3' }
                      }
                    }}
                  />
                  <TextField
                    label="Weight"
                    type="number"
                    value={freightInput.weight}
                    onChange={(e) => setFreightInput(prev => ({ ...prev, weight: e.target.value }))}
                    placeholder="0"
                    size="small"
                    sx={{
                      width: '100px',
                      '& .MuiInputLabel-root': { color: '#ccc' },
                      '& .MuiOutlinedInput-root': {
                        color: '#fff',
                        '& fieldset': { borderColor: '#555' },
                        '&:hover fieldset': { borderColor: '#777' },
                        '&.Mui-focused fieldset': { borderColor: '#2196f3' }
                      }
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleAddFreightItem}
                    size="small"
                    sx={{
                      minWidth: 'unset',
                      px: 1,
                      borderColor: '#F9B612',
                      color: '#F9B612',
                      '&:hover': {
                        borderColor: '#D49B0A',
                        backgroundColor: 'rgba(249, 182, 18, 0.1)'
                      }
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </Button>
                </Box>

                {/* Bin Distribution Inputs */}
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ color: '#999', mb: 0.5, display: 'block' }}>
                    Bin Distribution (optional)
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 0.5 }}>
                    {['A', 'B', 'C', 'D', 'E', 'F'].map(bin => (
                      <TextField
                        key={bin}
                        label={bin}
                        type="number"
                        value={freightInput.bins[bin]}
                        onChange={(e) => setFreightInput(prev => ({
                          ...prev,
                          bins: { ...prev.bins, [bin]: e.target.value }
                        }))}
                        size="small"
                        sx={{
                          '& .MuiInputLabel-root': { color: '#ccc', fontSize: '0.75rem' },
                          '& .MuiOutlinedInput-root': {
                            color: '#fff',
                            fontSize: '0.875rem',
                            '& fieldset': { borderColor: '#555' },
                            '&:hover fieldset': { borderColor: '#777' },
                            '&.Mui-focused fieldset': { borderColor: '#2196f3' }
                          },
                          '& .MuiInputBase-input': {
                            padding: '6px 8px'
                          }
                        }}
                      />
                    ))}
                  </Box>
                </Box>

                {/* Display Added Freight Items */}
                {customFlightData.freight && customFlightData.freight.length > 0 && (
                  <List sx={{ backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '8px', padding: 0, mt: 1 }}>
                    {customFlightData.freight.map((freight, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          borderBottom: index < customFlightData.freight.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none',
                          py: 1
                        }}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            onClick={() => handleDeleteCustomFreight(index)}
                            sx={{ color: '#f44336' }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={`${freight.type} ${freight.pieces}/${freight.weight}`}
                          primaryTypographyProps={{ sx: { color: '#F9B612', fontWeight: 'bold' } }}
                          secondary={
                            freight.bins && Object.keys(freight.bins).length > 0
                              ? `Bins: ${Object.entries(freight.bins)
                                  .filter(([_, count]) => count > 0)
                                  .map(([bin, count]) => `${bin}:${count}`)
                                  .join(', ')}`
                              : null
                          }
                          secondaryTypographyProps={{ sx: { color: '#ccc', fontSize: '0.75rem' } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>

              {/* Notes */}
              <TextField
                label="Notes"
                value={customFlightData.notes}
                onChange={(e) => setCustomFlightData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional notes"
                multiline
                rows={2}
                size="small"
                sx={{
                  '& .MuiInputLabel-root': { color: '#ccc' },
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: '#555' },
                    '&:hover fieldset': { borderColor: '#777' },
                    '&.Mui-focused fieldset': { borderColor: '#2196f3' }
                  }
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ backgroundColor: 'transparent' }}>
            <Button onClick={handleCloseCustomFlightModal} sx={{ color: '#ccc' }}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="custom-flight-form"
              variant="contained"
              sx={{
                backgroundColor: '#9C27B0',
                '&:hover': { backgroundColor: '#7B1FA2' }
              }}
            >
              Create Flight
            </Button>
          </DialogActions>
        </Dialog>

        {/* Flight Note Modal - Note for All Drivers */}
        <Dialog
          open={flightNoteModalOpen}
          onClose={() => setFlightNoteModalOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: 'rgba(31, 41, 55, 0.95)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              color: '#fff'
            }
          }}
        >
          <DialogTitle sx={{ color: '#F9B612', backgroundColor: 'transparent' }}>
            Note for All Drivers
          </DialogTitle>
          <DialogContent sx={{ backgroundColor: 'transparent' }}>
            <Typography variant="body2" sx={{ color: '#ccc', mb: 2 }}>
              This note will be added to every driver's notes field and appear on all driver printouts.
            </Typography>
            <TextField
              value={editingFlightNote}
              onChange={(e) => setEditingFlightNote(e.target.value)}
              placeholder="Enter a note for all drivers..."
              multiline
              rows={4}
              fullWidth
              autoFocus
              sx={{
                '& .MuiInputLabel-root': { color: '#ccc' },
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': { borderColor: '#555' },
                  '&:hover fieldset': { borderColor: '#777' },
                  '&.Mui-focused fieldset': { borderColor: '#F9B612' }
                }
              }}
            />
          </DialogContent>
          <DialogActions sx={{ backgroundColor: 'transparent' }}>
            <Button onClick={() => setFlightNoteModalOpen(false)} sx={{ color: '#ccc' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                // Update the flight note
                if (setFlightNote) {
                  setFlightNote(editingFlightNote);
                }
                // Update all existing drivers' notes
                setRuns(prevRuns => ({
                  ...prevRuns,
                  drivers: prevRuns.drivers.map(driver => ({
                    ...driver,
                    notes: editingFlightNote
                  }))
                }));
                setFlightNoteModalOpen(false);
              }}
              sx={{
                backgroundColor: '#F9B612',
                color: '#000',
                fontWeight: 'bold',
                '&:hover': { backgroundColor: '#e6a700' }
              }}
            >
              Apply to All Drivers
            </Button>
          </DialogActions>
        </Dialog>
      <RouteMapModal
        open={routeModalDriver !== null}
        onClose={() => setRouteModalDriver(null)}
        driver={routeModalDriver}
        inboundGate={inboundGate}
        inboundETA={inboundETA}
      />
      <ReviewPopup
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        items={reviewItems}
      />
      </Box>
    </Paper>
  );
}

export default DispatchBoard;
